<template>
  <div
    class="equipment-schedule"
    :class="{ 'right-menu-layout-open': rightMenuLayoutOpen }"
    :style="{
      '--schedule-right-menu-layout-width': `${rightMenuLayoutWidth || 420}px`,
    }"
  >
    <div class="gantt-container" ref="gantt-container">
      <div class="schedule-toolbar-card">
        <div class="schedule-toolbar-row schedule-toolbar-main">
          <div class="date-title">Show timeline</div>
          <div class="schedule-date-picker">
            <DateTimePicker
              ref="scheduleStartDatePicker"
              placeholder="from"
              :value="startDate"
              :showTime="false"
              @update:value="timelineStartDateChanged"
            />
          </div>
          <div class="schedule-date-picker">
            <DateTimePicker
              ref="scheduleEndDatePicker"
              placeholder="to"
              :value="endDate"
              :minDate="startDate"
              :showTime="false"
              @update:value="timelineEndDateChanged"
            />
          </div>
          <div class="schedule-toolbar-actions">
            <button
              ref="scheduleFiltersButton"
              type="button"
              class="filter-button"
              :disabled="!scheduleFiltersReady"
              :aria-busy="filterReferencesLoading ? 'true' : 'false'"
              :aria-expanded="showScheduleFilters ? 'true' : 'false'"
              aria-controls="schedule-filters-popup"
              aria-haspopup="dialog"
              @click="toggleScheduleFilters"
            >
              Filters
              <span v-if="activeFilterCount > 0" class="filter-count">{{ activeFilterCount }}</span>
            </button>
            <div ref="scheduleRefreshContainer" class="refresh-container">
              <button
                ref="scheduleRefreshButton"
                type="button"
                :disabled="scheduleDataLoading"
                :aria-expanded="showRefreshDropdown ? 'true' : 'false'"
                aria-controls="schedule-refresh-menu"
                aria-haspopup="menu"
                @click="toggleRefreshDropdown"
              >
                {{ scheduleDataLoading ? 'Refreshing...' : refreshText }}
              </button>
              <div
                v-if="showRefreshDropdown"
                id="schedule-refresh-menu"
                ref="scheduleRefreshMenu"
                class="refresh-dropdown"
                role="menu"
              >
                <div>Refresh Every:</div>
                <button type="button" role="menuitem" @click="refreshIntervalClicked($event, 10)">10 seconds</button>
                <button type="button" role="menuitem" @click="refreshIntervalClicked($event, 30)">30 seconds</button>
                <button type="button" role="menuitem" @click="refreshIntervalClicked($event, 300)">5 minutes</button>
                <button type="button" role="menuitem" @click="refreshIntervalClicked($event, 600)">10 minutes</button>
                <button type="button" role="menuitem" @click="refreshIntervalClicked($event, 1800)"
                  >30 minutes</button
                >
                <button type="button" role="menuitem" @click="refreshIntervalClicked($event, 3600)">1 hour</button>
                <button type="button" role="menuitem" class="refresh-now" @click="refreshIntervalClicked($event, null)"
                  >Refresh Now</button
                >
                <button
                  v-if="refreshInterval"
                  type="button"
                  role="menuitem"
                  class="cancel-refresh"
                  @click="cancelRefreshClicked"
                  >Cancel Refresh</button
                >
              </div>
            </div>
            <div class="last-refresh">Last refreshed: {{ lastRefresh }}</div>
          </div>
          <button
            v-if="isMenuVisible('New Schedule') && !isNewScheduleOpen"
            type="button"
            class="new-schedule-button"
            :disabled="!isMenuEnabled('New Schedule')"
            @click="menuClicked(menuByTitle('New Schedule'))"
          >
            New Schedule
          </button>
        </div>
        <div
          v-if="showScheduleFilters"
          class="schedule-filters-backdrop"
          :style="scheduleFiltersPosition"
          @click.self="closeScheduleFilters(false)"
        >
          <div
            id="schedule-filters-popup"
            ref="scheduleFiltersPopup"
            class="schedule-filters-popup"
            role="dialog"
            aria-label="Schedule filters"
          >
            <div class="schedule-filters-header">
              <strong>Schedule Filters</strong>
              <button type="button" aria-label="Close filters" title="Close filters" @click="closeScheduleFilters(true)"
                >x</button
              >
            </div>
            <div class="schedule-filters">
              <Filters
                title="Vehicle Category"
                itemKey="CategoryCode"
                itemDesc="CategoryDesc"
                :tableData="categoryFilterList"
                :selectedObject.sync="selectedCategories"
                noneSelectedText="All"
                noneSelectedConfirmButtonText="Select All"
                @confirmClicked="scheduleFilterChanged"
              />
              <Filters
                title="Source"
                itemKey="label"
                itemDesc="label"
                :tableData="sourceList"
                :selectedObject.sync="selectedSources"
                :tableBoxHideColumns = "['viewType', 'color', 'legendUrl']"
                noneSelectedText="All"
                noneSelectedConfirmButtonText="Select All"
                @confirmClicked="scheduleFilterChanged"
              />
              <Filters
                title="Customer"
                itemKey="label"
                itemDesc="label"
                :tableData="customerList"
                :selectedObject.sync="selectedCustomers"
                noneSelectedText="All"
                noneSelectedConfirmButtonText="Select All"
                @confirmClicked="scheduleFilterChanged"
              />
              <Filters
                title="Team"
                itemKey="label"
                itemDesc="label"
                :tableData="teamList"
                :selectedObject.sync="selectedTeams"
                noneSelectedText="All"
                noneSelectedConfirmButtonText="Select All"
                @confirmClicked="scheduleFilterChanged"
              />
              <Filters
                title="Collection Area"
                itemKey="label"
                itemDesc="label"
                :tableData="collectionAreaList"
                :selectedObject.sync="selectedCollectionAreas"
                noneSelectedText="All"
                noneSelectedConfirmButtonText="Select All"
                @confirmClicked="scheduleFilterChanged"
              />
              <Filters
                title="Delivery Area"
                itemKey="label"
                itemDesc="label"
                :tableData="deliveryAreaList"
                :selectedObject.sync="selectedDeliveryAreas"
                noneSelectedText="All"
                noneSelectedConfirmButtonText="Select All"
                @confirmClicked="scheduleFilterChanged"
              />
            </div>
            <div class="schedule-filters-footer">
              <button type="button" @click="clearFilters">Clear Filters</button>
              <button type="button" @click="closeScheduleFilters(true)">Done</button>
            </div>
          </div>
        </div>
      </div>
      <div class="schedule-chart-card" :aria-busy="scheduleDataLoading ? 'true' : 'false'">
        <div v-if="scheduleDataLoading" class="schedule-sr-only" role="status" aria-live="polite">
          Refreshing schedule data...
        </div>
        <div class="chart-toolbar">
          <div class="chart-toolbar-control">
            <div class="segmented-control" role="group" aria-label="Schedule view">
              <button type="button" :class="{ active: viewMode === 'timeline' }" @click="changeViewMode('timeline')"
                >Timeline</button
              >
              <button type="button" :class="{ active: viewMode === 'board' }" @click="changeViewMode('board')"
                >Cards</button
              >
            </div>
          </div>
          <div class="chart-toolbar-right">
            <div class="chart-toolbar-control schedule-search-control">
              <Input placeholder="Search" :inputValue.sync="scheduleSearchInput" />
            </div>
            <div class="chart-toolbar-control group-by-control">
              <Dropdown
                :items="timelineGroupOptions"
                itemKey="label"
                placeholder="Group By"
                :selectedObject.sync="activeGroupSelection"
                style="width: 170px"
              />
            </div>
          </div>
        </div>
        <div v-if="displaySourceErrors.length > 0" class="chart-status-row">
          <div class="chart-status-right">
            <div class="source-warning" :title="displaySourceErrors.join(', ')">
              Unavailable: {{ displaySourceErrors.join(', ') }}
            </div>
          </div>
        </div>
        <div class="gantt-with-legends">
          <GanttChartV4
            v-if="viewMode === 'timeline'"
            ref="scheduleGantt"
            :items="timelineGanttItems"
            :interval="timelineInterval"
            :rangeStart="startDate"
            :rangeEnd="endDate"
            :selectedTaskKey="selectedScheduleKey"
            :showCategoryRows="timelineGroupBy !== 'none'"
            :useSourceTaskBorder="timelineGroupBy !== 'none'"
            :showZoomControls="true"
            @taskClicked="ganttTaskClicked"
            @intervalChanged="timelineZoomChanged"
            @rowClicked="ganttRowClicked"
            :timeZoneWidth.sync="ganttMainWidth"
          />
          <div v-else class="schedule-board">
            <div v-if="boardGroups.length === 0" class="board-empty"
              >No scheduled events match the selected filters.</div
            >
            <div v-for="group in boardGroups" :key="group.key" class="board-group">
              <div class="board-group-title">{{ group.label }} <span>{{ group.items.length }}</span></div>
              <div class="board-cards">
                <button
                  v-for="item in group.items"
                  :key="item._scheduleKey"
                  type="button"
                  :class="['board-card', { 'board-card-selected': item._selectionKey === selectedScheduleKey }]"
                  :aria-pressed="item._selectionKey === selectedScheduleKey ? 'true' : 'false'"
                  :aria-label="scheduleItemAriaLabel(item)"
                  @click="ganttTaskClicked(item)"
                >
                  <div class="board-card-title" :title="boardCardTitle(item)">{{ boardCardTitle(item) }}</div>
                  <div class="board-card-tags">
                    <span class="board-card-tag" :style="{ '--board-tag-color': scheduleItemCategoryColor(item) }">
                      {{ item._sourceLabel || sourceLabel(item._source) }}
                    </span>
                    <span class="board-card-tag" :style="{ '--board-tag-color': scheduleItemColor(item) }">
                      {{ scheduleItemLegend(item) }}
                    </span>
                  </div>
                  <div class="board-card-window">
                    <strong>Time</strong>
                    <span>{{ formatScheduleDate(item.cbstart) }} - {{ formatScheduleEnd(item) }}</span>
                  </div>
                  <div class="board-card-details">
                    <div v-for="detail in boardCardDetails(item)" :key="detail.label" class="board-card-detail">
                      <strong>{{ detail.label }}</strong>
                      <span :title="detail.value">{{ detail.value }}</span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
          <button
            v-if="viewMode === 'timeline' && legendsCollapsed"
            type="button"
            class="gantt-legends-toggle"
            title="Show legends"
            @click="legendsCollapsed = false"
          >
            <img class="gantt-legends-chevron" src="https://dsmlib.logistics.systems/Icons/left-chevron.png" alt="" />
          </button>
          <div v-if="viewMode === 'timeline'" class="gantt-legends" :class="{ 'legends-collapsed': legendsCollapsed }">
            <div v-if="!legendsCollapsed" class="gantt-legend-header">
              <span>Legends</span>
              <button type="button" class="gantt-legends-close" title="Hide legends" @click="legendsCollapsed = true">
                <img class="gantt-legends-chevron" src="https://dsmlib.logistics.systems/Icons/left-chevron.png" alt="" />
              </button>
            </div>
            <template v-if="!legendsCollapsed">
              <div class="gantt-legend-group" v-for="group in legendGroups" :key="group.key">
                <div class="gantt-legend-group-title">
                  <span class="gantt-legend-group-color" :style="{ backgroundColor: group.color }"></span>
                  <span>{{ group.label }}</span>
                </div>
                <div class="gantt-legends-items" v-for="item in group.items" :key="item.key || item.ColorDesc">
                  <div class="gantt-legends-color" :style="{ backgroundColor: item.ColorHexa }"></div>
                  <div>{{ item.ColorDesc }}</div>
                </div>
              </div>
            </template>
            <div v-else class="legend-collapsed-swatches">
              <span v-for="group in legendGroups" :key="group.key" :title="group.label" :style="{ backgroundColor: group.color }"></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <RightMenu
      ref="scheduleRightMenu"
      :class="[
        'right-menu',
        {
          'right-menu-slide-open': rightMenuSlideOpen,
        },
      ]"
      :overlay="true"
      :maxWidth="550"
      :minWidth="330"
      :defaultWidth="420"
      :showCloseButton="true"
      :showMenu.sync="showRightMenu"
      :width="rightMenuWidth || 420"
      @update:width="rightMenuWidthChanged"
    >
      <div class="ems-title" :style="{ 'background-color': '#b3c4ff' }">
        {{ rightPanelTitle }}
      </div>

      <div v-if="rightPanelMode === 'actions'" class="schedule-action-panel">
        <div class="schedule-action-summary">{{ selectedScheduleTitle || 'No schedule selected' }}</div>
        <div class="schedule-action-details">
          <div><strong>Source</strong><span>{{ selectedScheduleDetails.source || '-' }}</span></div>
          <div><strong>Vehicle</strong><span>{{ selectedScheduleDetails.equipment || '-' }}</span></div>
          <div><strong>Category</strong><span>{{ selectedScheduleDetails.category || 'Unassigned' }}</span></div>
          <div><strong>Team</strong><span>{{ selectedScheduleDetails.team || 'Unassigned' }}</span></div>
          <div><strong>Customer</strong><span>{{ selectedScheduleDetails.customer || 'Unassigned' }}</span></div>
          <div><strong>From</strong><span>{{ selectedScheduleDetails.collectionArea || '-' }}</span></div>
          <div><strong>To</strong><span>{{ selectedScheduleDetails.deliveryArea || '-' }}</span></div>
          <div><strong>Time</strong><span>{{ selectedScheduleDetails.plannedSlot || '-' }}</span></div>
        </div>
        <div class="schedule-action-buttons">
          <button
            v-if="selectedScheduleIsEditable && isMenuVisible('Edit Schedule')"
            type="button"
            :disabled="!isMenuEnabled('Edit Schedule')"
            @click="menuClicked(menuByTitle('Edit Schedule'))"
          >
            Edit Schedule
          </button>
          <button
            v-if="selectedScheduleIsEditable && isMenuVisible('Delete Schedule')"
            type="button"
            :disabled="!isMenuEnabled('Delete Schedule') || deleteScheduleLoading"
            @click="menuClicked(menuByTitle('Delete Schedule'))"
          >
            {{ deleteScheduleLoading ? 'Deleting...' : 'Delete Schedule' }}
          </button>
        </div>
      </div>
      <div
        v-else
        class="ems-trip-container"
        :class="{ 'is-loading': editScheduleLoading || saveScheduleLoading }"
        :aria-busy="editScheduleLoading || saveScheduleLoading ? 'true' : 'false'"
      >
        <div v-if="editScheduleLoading" class="edit-schedule-loading" role="status" aria-live="polite">
          <span class="edit-schedule-spinner"></span>
          <span>Loading schedule details...</span>
        </div>
        <div class="rowBox">
          <div class="schedule-form-field schedule-form-date-time">
            <DateTimePicker
              :isDisabled="popupViewMode || editScheduleLoading || saveScheduleLoading"
              placeholder="Start Date"
              :value.sync="starttime"
              :errorMessage.sync="starttimeError"
            />
          </div>
          <div class="schedule-form-field schedule-form-date-time">
            <DateTimePicker
              :isDisabled="popupViewMode || editScheduleLoading || saveScheduleLoading"
              placeholder="End Date"
              defaultTime="23:59"
              :value.sync="endtime"
              :errorMessage.sync="endtimeError"
            />
          </div>
        </div>
        <div class="schedule-form-field">
          <InputQuery
            placeholder="From Location"
            :items="destinationList"
            itemKey="Destination Code"
            descKey="fromLoc"
            :selectedObject.sync="fromLoc"
            :isDisabled="popupViewMode || editScheduleLoading || saveScheduleLoading"
            :isRequired="true"
            :errorMessage.sync="fromLocError"
          />
        </div>

        <div class="schedule-form-field">
          <InputQuery
            placeholder="To Location"
            :items="destinationList"
            itemKey="Destination Code"
            descKey="toLoc"
            :selectedObject.sync="toLoc"
            :isDisabled="popupViewMode || editScheduleLoading || saveScheduleLoading"
            :isRequired="true"
            :errorMessage.sync="toLocError"
          />
        </div>

        <div class="schedule-form-field">
          <InputQuery
            placeholder="Reason Type"
            :items="reasoningList"
            itemKey="Reason Type"
            descKey="reason"
            :selectedObject.sync="selectedReason"
            :isDisabled="popupViewMode || editScheduleLoading || saveScheduleLoading"
            :isRequired="true"
            :errorMessage.sync="reasonError"
          />
        </div>

        <div class="schedule-form-field">
          <InputQuery
            placeholder="Availability"
            :items="availabilityList"
            itemKey="Availability"
            descKey="availability"
            :selectedObject.sync="selectedAvailability"
            :isDisabled="popupViewMode || editScheduleLoading || saveScheduleLoading"
            :isRequired="true"
            :errorMessage.sync="availabilityError"
          />
        </div>

        <div class="schedule-form-field">
          <Input
            placeholder="Remarks"
            :inputValue.sync="remarks"
            :isDisabled="popupViewMode || editScheduleLoading || saveScheduleLoading"
          />
        </div>
        <div
          v-if="isNewScheduleOpen"
          class="vehicle-multiselect"
          :class="{ 'is-disabled': popupViewMode || editScheduleLoading || saveScheduleLoading }"
          :aria-disabled="popupViewMode || editScheduleLoading || saveScheduleLoading ? 'true' : 'false'"
        >
          <MultiSelect
            :items="vehicleList"
            :value="selectedVehicle"
            itemKey="Inventory #"
            saveKey="Inventory #"
            itemDesc="vehicle"
            placeholder="Vehicles Selected"
            @update:value="filterConfirm"
          />
        </div>
        <div v-else class="scheduled-vehicle-readonly">
          <div class="scheduled-vehicle-label">Scheduled Vehicle</div>
          <div class="scheduled-vehicle-value">{{ scheduledVehicleLabel }}</div>
        </div>

        <div class="ems-footer">
          <button
            type="button"
            :disabled="editScheduleLoading || saveScheduleLoading"
            @click="saveClicked"
          >
            {{ saveScheduleLoading ? 'Saving...' : 'Save' }}
          </button>
          <button type="button" :disabled="saveScheduleLoading" @click="closeMenu">Cancel</button>
        </div>
      </div>
    </RightMenu>

  </div>
</template>

<script>
/* eslint-disable */
import MultiSelect from '@/components/v2testComponent/MultiSelect.vue';

export default {
  components: {
    MultiSelect,
    //REMAINING WORK
    //SAS control //done
    //error message for vehicle
    //display "no vehicle selected" as default //done
    //auto close task details
    //top left container shouldnt move with the left menu
  },
  data: function () {
    const today = new Date();
    const weekStart = new Date(today);
    weekStart.setHours(0, 0, 0, 0);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    return {
      //UI items
      ganttItems: [],
      startDate: weekStart,
      endDate: weekEnd,
      popupTitle: '',
      legendsItems: [],
      moduleLegendItems: {
        EMS: [],
        HMS: [],
        TMS: [],
        EXPIRY: [],
      },
      lastRefresh: '',
      viewMode: 'timeline',
      timelineInterval: 720,
      timelineGroupBy: 'category',
      boardGroupBy: 'category',
      scheduleSearchInput: '',
      scheduleSearchTerm: '',
      scheduleSearchTimer: 0,
      timelineGroupOptions: [
        { key: 'none', label: 'None' },
        { key: 'category', label: 'Vehicle Category' },
        { key: 'team', label: 'Team' },
        { key: 'collectionArea', label: 'Collection Area' },
        { key: 'deliveryArea', label: 'Delivery Area' },
        { key: 'customer', label: 'Customer' },
        { key: 'plannedStartDate', label: 'Planned Start Date' },
        { key: 'plannedEndDate', label: 'Planned End Date' },
      ],
      timelineGroupSelection: { key: 'category', label: 'Vehicle Category' },
      boardGroupSelection: { key: 'category', label: 'Vehicle Category' },
      popupViewMode: false,
     ganttMainWidth: 0,
     menuItems: [
        { key: 'New', title: 'New Schedule', icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png' },
        { key: 'Edit', title: 'Edit Schedule', icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png' },
        { key: 'Delete', title: 'Delete Schedule', icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png' },
     ],
      showRightMenu: false,
      rightMenuWidth: 0,
      rightMenuLayoutWidth: 420,
      rightMenuLayoutOpen: false,
      rightMenuSlideOpen: false,
      ganttLayoutRefreshTimer: 0,
      rightMenuResizeTimer: 0,
      rightMenuResizeActive: false,
      rightMenuResizePointerDown: false,
      rightMenuResizeScrollLeft: 0,
      showemsTrip: false, // screen showing all trips, right before clicking Port Booking / Depot Booking
      emsTitle: '',
      emsLoading: false,
      emsTrips: [],
      consignmentDetails: null,
      showRefreshDropdown: false,
      showScheduleFilters: false,
      scheduleFiltersPosition: {},
      editScheduleLoading: false,
      saveScheduleLoading: false,
      deleteScheduleLoading: false,
      scheduleDataLoading: false,
      filterReferencesLoading: true,
      filterReferencesFailed: false,
      rightPanelMode: 'actions',
      legendsCollapsed: false,
      refreshTime: 0,
      refreshTimeStart: 0,
      refreshInterval: null,

      //EMS DATA
      availabilityList: [],
      reasoningList: [],
      categoryList: [],
      selectedCategories: [],
      sourceList: [
        {
          key: 'EMS',
          label: 'Workshop Job',
          viewType: 'EMS_SCHEDULE',
          color: '#4f81bd',
          legendUrl: '/EquipmentSchedule/GetLegends',
        },
        {
          key: 'EXPIRY',
          label: 'Policy expiry',
          viewType: 'POLICY',
          color: '#c00000',
          legendUrl: '/EquipmentSchedule/GetLegends?viewType=POLICY',
        },
        {
          key: 'HMS',
          label: 'Haulage Job',
          viewType: 'TEAM_PM',
          color: '#70ad47',
          legendUrl: '/VehicleSchedule/GetLegends?viewType=TEAM_PM',
        },
        {
          key: 'TMS',
          label: 'Trucking Job',
          viewType: 'TEAM_TR',
          color: '#ed7d31',
          legendUrl: '/TruckingSchedule/GetLegends?viewType=TEAM_TR',
        },
      ],
      selectedSources: [],
      selectedCustomers: [],
      selectedTeams: [],
      selectedCollectionAreas: [],
      selectedDeliveryAreas: [],
      sourceErrors: [],
      referenceErrors: [],
      selectedSchedule: [],
      destinationList: [],
      vehicleList: [],
      vehicleGroupingList: [],

      //data input
      selectedAvailability: null,
      selectedReason: null,
      selectedVehicle: [],
      selectedVehicleCode: [],
      remarks: '',
      starttime: null,
      endtime: null,
      fromLoc: null,
      toLoc: null,
      scheduleID: '',

      //input validation
      starttimeError: '',
      endtimeError: '',
      reasonError: '',
      availabilityError: '',
      fromLocError: '',
      toLocError: '',
      entryRequired: [],
      entrySuccess: [],
      entryMessages: [],
      entryError: [],
    };
  },
  props: {},
  computed: {
    categoryMasterLookup: function () {
      return this.categoryList.reduce((result, category) => {
        const code = this.valueFrom(category, ['CategoryCode', 'Category', 'categorycode']);
        if (code) {
          const description = this.valueFrom(category, ['CategoryDesc', 'Category Description', 'Description']) || code;
          result[this.categoryKey(code)] = {
            code: String(code).trim(),
            description: String(description).trim(),
            label: this.combinedGanttColumnValue(code, description),
          };
        }
        return result;
      }, {});
    },
    vehicleGroupingLookup: function () {
      return this.vehicleGroupingList.reduce((result, vehicle) => {
        const lorryNo = this.valueFrom(vehicle, ['Lorry No.', 'Lorry No', 'Lorry_No', 'Inventory #']);
        const vehicleCode = this.valueFrom(vehicle, ['Vehicle Code', 'VehicleCode', 'vehiclecode']);
        const category = this.valueFrom(vehicle, ['Vehicle Category', 'VehicleCategory', 'Category', 'CategoryCode']);
        const team = this.valueFrom(vehicle, ['Team', 'team', 'Team Name', 'TeamName']);
        const vehicleKey = this.normalizedKey(lorryNo || vehicleCode);
        const entry = {
          lorryNo: String(lorryNo || '').trim(),
          vehicleCode: String(vehicleCode || '').trim(),
          vehicleKey: vehicleKey,
          category: String(category || '').trim(),
          team: String(team || '').trim(),
        };
        [
          lorryNo,
          vehicleCode,
          lorryNo && vehicleCode ? `${lorryNo}-${vehicleCode}` : '',
          lorryNo && vehicleCode ? `${vehicleCode}-${lorryNo}` : '',
          lorryNo && vehicleCode ? this.combinedGanttColumnValue(lorryNo, vehicleCode) : '',
          lorryNo && vehicleCode ? this.combinedGanttColumnValue(vehicleCode, lorryNo) : '',
        ].forEach((candidate) => {
          const key = this.normalizedKey(candidate);
          if (key) {
            result[key] = entry;
          }
        });
        return result;
      }, {});
    },
    categoryFilterList: function () {
      const categories = {};
      let hasUnassigned = false;
      this.categoryList.forEach((category) => {
        const code = this.valueFrom(category, ['CategoryCode', 'Category', 'categorycode']);
        if (code) {
          const key = this.categoryKey(code);
          categories[key] = {
            CategoryCode: code,
            CategoryDesc: this.valueFrom(category, ['CategoryDesc', 'Category Description', 'Description']) || code,
          };
        }
      });
      this.normalizedGanttItems.forEach((item) => {
        if (item._category) {
          const key = this.categoryKey(item._category);
          if (!categories[key]) {
            categories[key] = {
              CategoryCode: item._category,
              CategoryDesc: item._categoryLabel || item._category,
            };
          }
        } else {
          hasUnassigned = true;
        }
      });
      if (hasUnassigned) {
        categories.UNASSIGNED = { CategoryCode: 'UNASSIGNED', CategoryDesc: 'Unassigned' };
      }
      return Object.keys(categories)
        .sort()
        .map((key) => categories[key]);
    },
    normalizedGanttItems: function () {
      return this.ganttItems.map((item, index) => this.normalizeScheduleItem(item, index));
    },
    filteredGanttItems: function () {
      const categoryKeys = this.selectedCategories.map((item) =>
        this.categoryKey(item.CategoryCode || item.Category || item.label),
      );
      const sourceKeys = this.selectedSources.map((item) => this.normalizedKey(item.key || item.label));
      const customerKeys = this.selectedCustomers.map((item) => this.normalizedKey(item.key || item.label));
      const teamKeys = this.selectedTeams.map((item) => this.normalizedKey(item.key || item.label));
      const collectionAreaKeys = this.selectedCollectionAreas.map((item) => this.normalizedKey(item.key || item.label));
      const deliveryAreaKeys = this.selectedDeliveryAreas.map((item) => this.normalizedKey(item.key || item.label));

      return this.normalizedGanttItems.filter((item) => {
        const category = this.categoryKey(item._category || 'UNASSIGNED');
        const source = this.normalizedKey(item._source);
        const customer = this.normalizedKey(item._customer || 'UNASSIGNED');
        const team = this.normalizedKey(item._team || 'UNASSIGNED');
        const collectionArea = this.normalizedKey(item._collectionArea || 'UNASSIGNED');
        const deliveryArea = this.normalizedKey(item._deliveryArea || 'UNASSIGNED');
        return (
          this.scheduleOverlapsRange(item) &&
          (categoryKeys.length === 0 || categoryKeys.indexOf(category) >= 0) &&
          (sourceKeys.length === 0 || sourceKeys.indexOf(source) >= 0) &&
          (customerKeys.length === 0 || customerKeys.indexOf(customer) >= 0) &&
          (teamKeys.length === 0 || teamKeys.indexOf(team) >= 0) &&
          (collectionAreaKeys.length === 0 || collectionAreaKeys.indexOf(collectionArea) >= 0) &&
          (deliveryAreaKeys.length === 0 || deliveryAreaKeys.indexOf(deliveryArea) >= 0)
        );
      });
    },
    timelineGanttItems: function () {
      return this.searchedGanttItems.map((item) => {
        const groupValue = this.timelineGroupBy === 'none' ? '' : this.groupValueForItem(item, this.timelineGroupBy);
        const groupedItem = Object.assign({}, item);
        groupedItem.pcol1text = groupValue ? String(groupValue).trim() : '';
        groupedItem.pcol2text = '';
        groupedItem.pcol3text = '';
        groupedItem.cid = item._vehicleKey;
        return groupedItem;
      });
    },
    customerList: function () {
      return this.uniqueScheduleValues('_customer');
    },
    teamList: function () {
      return this.uniqueScheduleValues('_team');
    },
    collectionAreaList: function () {
      return this.uniqueScheduleValues('_collectionArea');
    },
    deliveryAreaList: function () {
      return this.uniqueScheduleValues('_deliveryArea');
    },
    searchedGanttItems: function () {
      const searchTerm = this.normalizedKey(this.scheduleSearchTerm);
      if (!searchTerm) {
        return this.filteredGanttItems;
      }
      return this.filteredGanttItems.filter((item) => this.scheduleItemMatchesSearch(item, searchTerm));
    },
    boardGroups: function () {
      const groups = {};
      this.searchedGanttItems.forEach((item) => {
        const groupValue = this.boardGroupValue(item);
        const isUngrouped = this.boardGroupBy === 'none';
        const key = isUngrouped ? 'ALL_SCHEDULES' : groupValue || 'Unassigned';
        if (!groups[key]) {
          groups[key] = { key: key, label: isUngrouped ? 'All Schedules' : groupValue || 'Unassigned', items: [] };
        }
        groups[key].items.push(item);
      });
      return Object.keys(groups)
        .sort((left, right) => {
          const leftUnassigned = this.normalizedKey(left) === 'UNASSIGNED';
          const rightUnassigned = this.normalizedKey(right) === 'UNASSIGNED';
          if (leftUnassigned !== rightUnassigned) {
            return leftUnassigned ? 1 : -1;
          }
          return left.localeCompare(right);
        })
        .map((key) => groups[key]);
    },
    displayLegends: function () {
      const legends =
        Array.isArray(this.legendsItems) ?
          this.legendsItems.map((item) => Object.assign({}, item, { ColorDesc: this.sourceLabel(item.ColorDesc) }))
        : [];
      const known = legends.map((item) => this.normalizedKey(item.ColorDesc));
      this.sourceList.forEach((source) => {
        if (known.indexOf(this.normalizedKey(source.label)) < 0) {
          legends.push({ ColorDesc: source.label, ColorHexa: this.sourceColor(source.key) });
        }
      });
      return legends;
    },
    legendGroups: function () {
      return this.sourceList.map((source) => {
        const rawItems = Array.isArray(this.moduleLegendItems[source.key]) ? this.moduleLegendItems[source.key] : [];
        const items = rawItems.map((item, index) => ({
          key: `${source.key}-${index}-${this.normalizedKey(item.ColorDesc)}`,
          ColorDesc: this.formatLegendLabel(this.sourceLabel(item.ColorDesc)),
          ColorHexa: item.ColorHexa || this.sourceColor(source.key),
        }));
        if (items.length === 0) {
          items.push({
            key: `${source.key}-source`,
            ColorDesc: this.formatLegendLabel(source.label),
            ColorHexa: this.sourceColor(source.key),
          });
        }
        return { key: source.key, label: source.label, color: this.sourceColor(source.key), items: items };
      });
    },
    activeFilterCount: function () {
      return (
        this.selectedCategories.length +
        this.selectedSources.length +
        this.selectedCustomers.length +
        this.selectedTeams.length +
        this.selectedCollectionAreas.length +
        this.selectedDeliveryAreas.length
      );
    },
    scheduleFiltersReady: function () {
      return !this.filterReferencesLoading && !this.filterReferencesFailed && !this.scheduleDataLoading;
    },
    displaySourceErrors: function () {
      return this.sourceErrors.concat(this.referenceErrors).filter((value, index, values) => {
        return value && values.indexOf(value) === index;
      });
    },
    selectedTimelineGroupOption: function () {
      return (
        this.timelineGroupOptions.find((option) => option.key === this.timelineGroupBy) || this.timelineGroupOptions[0]
      );
    },
    activeGroupSelection: {
      get: function () {
        return this.viewMode === 'timeline' ? this.timelineGroupSelection : this.boardGroupSelection;
      },
      set: function (option) {
        if (this.viewMode === 'timeline') {
          this.selectTimelineGroup(option);
        } else {
          this.selectBoardGroup(option);
        }
      },
    },
    hasSelectedSchedule: function () {
      return (
        !!this.selectedSchedule &&
        !Array.isArray(this.selectedSchedule) &&
        Object.keys(this.selectedSchedule).length > 0
      );
    },
    selectedScheduleKey: function () {
      return this.hasSelectedSchedule ? this.scheduleSelectionKey(this.selectedSchedule) : '';
    },
    selectedScheduleIsEditable: function () {
      return this.hasSelectedSchedule && this.scheduleSource(this.selectedSchedule) === 'EMS';
    },
    selectedScheduleSummary: function () {
      if (!this.hasSelectedSchedule) {
        return '';
      }
      const item = this.selectedSchedule;
      const source = item._sourceLabel || this.sourceLabel(item._source);
      const equipment = item._equipment || this.scheduleVehicleNumber(item);
      const title = String(item.cbtext || '')
        .replace(/\s*-\s*$/, '')
        .trim();
      const start = this.formatScheduleDate(item.cbstart);
      const end = this.formatScheduleEnd(item);
      const timeRange = start ? `${start}${end ? ` - ${end}` : ''}` : '';
      return [source, equipment, title, timeRange].filter((value) => value).join(' | ');
    },
    selectedScheduleTitle: function () {
      if (!this.hasSelectedSchedule) {
        return '';
      }
      return String(this.selectedSchedule.cbtext || '')
        .replace(/\s*-\s*$/, '')
        .trim();
    },
    selectedScheduleDetails: function () {
      if (!this.hasSelectedSchedule) {
        return {
          source: '',
          equipment: '',
          category: '',
          team: '',
          customer: '',
          collectionArea: '',
          deliveryArea: '',
          plannedSlot: '',
        };
      }
      const item = this.selectedSchedule;
      return {
        source: item._sourceLabel || this.sourceLabel(item._source),
        equipment: item._equipment || this.scheduleVehicleNumber(item),
        category: item._categoryLabel || item._category,
        team: item._team,
        customer: item._customer,
        collectionArea: item._collectionArea,
        deliveryArea: item._deliveryArea,
        plannedSlot: item._plannedSlot || this.plannedSlotValue(item),
      };
    },
    rightPanelTitle: function () {
      if (this.rightPanelMode === 'actions') {
        return 'Schedule Details';
      }
      return this.editScheduleTitle;
    },
    isNewScheduleOpen: function () {
      return this.showRightMenu && this.rightPanelMode === 'edit' && this.popupTitle === 'New Schedule';
    },
    scheduledVehicleLabel: function () {
      const vehicle = this.selectedVehicle[0];
      if (vehicle) {
        const inventory = vehicle['Inventory #'] || this.scheduleVehicleNumber(vehicle);
        const description = vehicle['vehicle'];
        return description && description !== inventory ? `${inventory} - ${description}` : inventory;
      }
      return this.selectedVehicleCode[0] || this.selectedScheduleDetails.equipment || 'Unassigned';
    },
    editScheduleTitle: function () {
      if (this.popupTitle !== 'Edit Schedule' || !this.selectedScheduleSummary) {
        return this.popupTitle;
      }
      const summary = this.selectedScheduleSummary.replace(/\s*\u00c2?\u00b7\s*/g, ' | ');
      return `Edit Schedule - ${summary}`;
    },
    refreshText: function () {
      if (!this.refreshInterval) {
        return 'Refresh';
      } else {
        let totalSeconds = this.refreshTime;
        let inMinute = Math.floor(totalSeconds / 60);

        if (totalSeconds >= 60) {
          return `Refresh in ${inMinute} minute${inMinute > 1 ? 's' : ''}`;
        } else {
          return `Refresh in ${this.refreshTime} seconds`;
        }
      }
    },
  },
  methods: {
    getScheduleGanttScrollElement: function () {
      const gantt = this.$refs.scheduleGantt;
      return gantt && gantt.$refs ? gantt.$refs.ganttMain : null;
    },
    clampRightMenuWidth: function (width) {
      const numericWidth = Number(width);
      return Math.max(330, Math.min(550, isFinite(numericWidth) ? numericWidth : 420));
    },
    getScheduleRightMenuElement: function () {
      const rightMenu = this.$refs.scheduleRightMenu;
      return rightMenu ? rightMenu.$el || rightMenu : null;
    },
    applyRightMenuLiveWidth: function (width, reserveLayoutSpace) {
      const clampedWidth = this.clampRightMenuWidth(width);
      const widthText = `${clampedWidth}px`;
      const rightMenuElement = this.getScheduleRightMenuElement();
      const ganttContainer = this.$refs['gantt-container'];

      // Update only the two DOM nodes whose geometry actually changes.
      // Do not assign reactive Vue data here: EquipmentSchedule is large, and
      // rerendering it for every mousemove is the main source of resize lag.
      if (rightMenuElement) {
        rightMenuElement.style.setProperty('--right-menu-live-width', widthText);
        rightMenuElement.style.width = widthText;
        rightMenuElement.style.minWidth = widthText;
        rightMenuElement.style.maxWidth = widthText;
      }

      if (ganttContainer) {
        // The left edge remains fixed. Only the right side of the viewport is
        // reduced, so the existing fixed-width Gantt timeline simply overflows.
        ganttContainer.style.marginRight = reserveLayoutSpace ? widthText : '';
      }

      return clampedWidth;
    },
    flushRightMenuLiveWidth: function () {
      if (this._rightMenuResizeFrame) {
        window.cancelAnimationFrame(this._rightMenuResizeFrame);
        this._rightMenuResizeFrame = 0;
      }

      const width = this.clampRightMenuWidth(this._pendingRightMenuWidth || this.rightMenuWidth || 420);
      this._rightMenuLiveWidth = width;
      this.applyRightMenuLiveWidth(width, this.rightMenuLayoutOpen || this.showRightMenu);
    },
    rightMenuWidthChanged: function (width) {
      const clampedWidth = this.clampRightMenuWidth(width);
      this._pendingRightMenuWidth = clampedWidth;

      if (!this._rightMenuResizeActive) {
        this._rightMenuResizeActive = true;
        const scrollElement = this.getScheduleGanttScrollElement();
        this._rightMenuResizeScrollLeft = scrollElement ? scrollElement.scrollLeft : 0;
      }

      // Coalesce potentially hundreds of emitted drag events into at most one
      // DOM write per animation frame.
      if (this._rightMenuResizeFrame) {
        return;
      }

      this._rightMenuResizeFrame = window.requestAnimationFrame(() => {
        this._rightMenuResizeFrame = 0;
        this.flushRightMenuLiveWidth();

        const scrollElement = this.getScheduleGanttScrollElement();
        if (scrollElement && this._rightMenuResizeScrollLeft != null) {
          scrollElement.scrollLeft = this._rightMenuResizeScrollLeft;
        }
      });
    },
    commitRightMenuLayoutWidth: function () {
      if (!this._rightMenuResizeActive) {
        return;
      }

      this.flushRightMenuLiveWidth();
      const width = this.clampRightMenuWidth(this._rightMenuLiveWidth || this.rightMenuWidth || 420);
      this._rightMenuResizeActive = false;
      this._pendingRightMenuWidth = 0;

      // Commit once after dragging ends. One Vue update is inexpensive; doing
      // the same assignment for every mousemove is not.
      this.rightMenuWidth = width;
      this.rightMenuLayoutWidth = width;

      this.$nextTick(() => {
        const scrollElement = this.getScheduleGanttScrollElement();
        if (scrollElement && this._rightMenuResizeScrollLeft != null) {
          scrollElement.scrollLeft = this._rightMenuResizeScrollLeft;
        }

        const gantt = this.$refs.scheduleGantt;
        if (gantt && gantt.updateGoToNowVisibility) {
          gantt.updateGoToNowVisibility();
        }
      });
    },
    rightMenuPointerPressed: function () {
      this._rightMenuResizePointerDown = true;
    },
    rightMenuPointerReleased: function () {
      this._rightMenuResizePointerDown = false;
      this.commitRightMenuLayoutWidth();
    },
    getModuleLegendItems: async function (path) {
      try {
        const result = await this.$apiCallV2(path);
        return Array.isArray(result) ? result : [];
      } catch (error) {
        return [];
      }
    },
    valueFrom: function (item, keys) {
      if (!item) {
        return '';
      }
      for (let i = 0; i < keys.length; i++) {
        const value = item[keys[i]];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          return value;
        }
      }
      return '';
    },
    normalizedKey: function (value) {
      return String(value || '')
        .trim()
        .toUpperCase();
    },
    categoryKey: function (value) {
      const text = String(value || '').trim();
      const match = text.match(/\(([^()]+)\)\s*$/);
      const bracketMatch = text.match(/^\[([^\]]+)\]/);
      return this.normalizedKey(
        match ? match[1]
        : bracketMatch ? bracketMatch[1]
        : text,
      );
    },
    vehicleGroupingForItem: function (item, equipment) {
      const candidates = [
        this.valueFrom(item, ['Inventory #', 'Vehicle No', 'Vehicle No.', 'Lorry No.', 'Lorry_No', 'Vehicle']),
        this.valueFrom(item, ['Vehicle Code', 'VehicleCode', 'vehiclecode']),
        item.ccol1text,
        equipment,
      ];
      for (let i = 0; i < candidates.length; i++) {
        const candidate = String(candidates[i] || '').trim();
        const keys = [candidate];
        const bracketMatch = candidate.match(/^\[([^\]]+)\]/);
        if (bracketMatch) {
          keys.push(bracketMatch[1]);
        }
        if (candidate.indexOf('-') >= 0) {
          candidate.split('-').forEach((part) => keys.push(part));
        }
        for (let j = 0; j < keys.length; j++) {
          const match = this.vehicleGroupingLookup[this.normalizedKey(keys[j])];
          if (match) {
            return match;
          }
        }
      }
      return null;
    },
    categoryDisplayValue: function (category, fallbackDescription) {
      const value = String(category || '').trim();
      if (!value) {
        return '';
      }
      const masterCategory = this.categoryMasterLookup[this.categoryKey(value)];
      if (masterCategory) {
        return masterCategory.label;
      }
      return this.combinedGanttColumnValue(value, fallbackDescription);
    },
    sourceColor: function (source) {
      const colors = { EMS: '#4f81bd', HMS: '#70ad47', TMS: '#ed7d31', EXPIRY: '#c00000' };
      return colors[this.normalizedKey(source)] || '#7f7f7f';
    },
    scheduleItemColor: function (item) {
      if (item && parseFloat(item.cbprogress) >= 1) {
        return item.cbprogresscolor || item.cbcolor || item.cbsourcecolor || this.sourceColor(item._source);
      }
      return (item && (item.cbcolor || item.cbprogresscolor || item.cbsourcecolor)) || this.sourceColor(item && item._source);
    },
    scheduleItemCategoryColor: function (item) {
      return (item && item.cbsourcecolor) || this.sourceColor(item && item._source);
    },
    scheduleItemLegend: function (item) {
      const color = String(this.scheduleItemColor(item) || '').trim().toLowerCase();
      const legendItems = item && Array.isArray(this.moduleLegendItems[item._source]) ? this.moduleLegendItems[item._source] : [];
      const legendItem = legendItems.find((entry) => String(entry.ColorHexa || '').trim().toLowerCase() === color);
      if (legendItem && legendItem.ColorDesc) {
        return this.formatLegendLabel(legendItem.ColorDesc);
      }
      return this.formatLegendLabel((item && item._eventType) || 'Scheduled job');
    },
    boardCardTitle: function (item) {
      return String((item && (item.cbtext || item._equipment || this.scheduleVehicleNumber(item))) || 'Scheduled event').trim();
    },
    scheduleItemMatchesSearch: function (item, searchTerm) {
      return [
        this.boardCardTitle(item),
        this.scheduleJobNumber(item),
        item && item._equipment,
        this.scheduleVehicleNumber(item),
        item && item._categoryLabel,
        item && item._category,
        item && item._sourceLabel,
        item && item._team,
        item && item._customer,
        item && item._collectionArea,
        item && item._deliveryArea,
        item && item._plannedStartDate,
        item && item._plannedEndDate,
        item && item._plannedSlot,
      ].some((value) => this.normalizedKey(value).indexOf(searchTerm) >= 0);
    },
    boardCardDetails: function (item) {
      const details = [];
      const category = String((item && (item._categoryLabel || item._category)) || '').trim();
      const equipment = String((item && item._equipment) || this.scheduleVehicleNumber(item) || '').trim();
      const collection = String((item && item._collectionArea) || '').trim();
      const delivery = String((item && item._deliveryArea) || '').trim();
      const team = String((item && item._team) || '').trim();
      const customer = String((item && item._customer) || '').trim();

      if (category) {
        details.push({ label: 'Category', value: category });
      }
      if (equipment) {
        details.push({ label: 'Vehicle', value: equipment });
      }
      if (collection || delivery) {
        details.push({ label: 'Route', value: `${collection || 'Unassigned'} - ${delivery || 'Unassigned'}` });
      }
      if (team) {
        details.push({ label: 'Team', value: team });
      }
      if (customer) {
        details.push({ label: 'Customer', value: customer });
      }
      if (details.length === 0) {
        details.push({ label: 'Assignment', value: 'Unassigned' });
      }
      return details;
    },
    sourceLabel: function (source) {
      const labels = { EMS: 'Workshop Job', HMS: 'Haulage Job', TMS: 'Trucking Job', EXPIRY: 'Policy expiry' };
      const key = this.normalizedKey(source);
      return labels[key] || source;
    },
    formatLegendLabel: function (value) {
      return String(value || '')
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/\s*\/\s*/g, ' / ')
        .replace(/\s*-\s*/g, ' - ')
        .replace(/\s+/g, ' ')
        .trim();
    },
    columnValueByTitle: function (item, terms) {
      if (!item) {
        return '';
      }
      const columns = [1, 2, 3].map((index) => ({
        title: this.valueFrom(item, [`pcol${index}title`, `ccol${index}title`, `col${index}title`]),
        value: this.valueFrom(item, [`pcol${index}text`, `ccol${index}text`]),
      }));
      const matched = columns.find((column) => {
        const title = this.normalizedKey(column.title);
        return column.value && terms.some((term) => title.indexOf(this.normalizedKey(term)) >= 0);
      });
      return matched ? matched.value : '';
    },
    combinedGanttColumnValue: function (code, description) {
      const codeText = String(code || '').trim();
      const descriptionText = String(description || '').trim();
      if (codeText && descriptionText) {
        return `[${codeText}] ${descriptionText}`;
      }
      return codeText || descriptionText;
    },
    ganttSideValue: function (value) {
      const text = String(value || '').trim();
      if (!text) {
        return '';
      }
      const parts = text.split(/\s+/);
      const prefix = this.normalizedKey(parts[0]);
      const movementPrefixes = [
        'COL',
        'COLLECT',
        'COLLECTION',
        'DEL',
        'DELIVER',
        'DELIVERY',
        'PICKUP',
        'DROPOFF',
        'FROM',
        'TO',
      ];
      return parts.length > 1 && movementPrefixes.indexOf(prefix) >= 0 ? parts.slice(1).join(' ') : text;
    },
    plannedSlotValue: function (item) {
      const start = this.formatScheduleDate(item && item.cbstart);
      const end = this.formatScheduleEnd(item || {});
      if (!start) {
        return '';
      }
      return end ? `${start} - ${end}` : start;
    },
    scheduleVehicleNumber: function (item) {
      return String(
        this.valueFrom(item, [
          '_equipment',
          'Inventory #',
          'Vehicle No',
          'Vehicle No.',
          'Lorry No.',
          'Lorry_No',
          'Vehicle',
          'ccol1text',
          'pcol1text',
        ]) || '',
      ).trim();
    },
    scheduleSource: function (item) {
      const source = String(
        this.valueFrom(item, ['source', 'Source', 'sourceSystem', 'SourceSystem', 'system', 'System']) || '',
      )
        .trim()
        .toUpperCase();
      if (source.indexOf('EXPIRY') >= 0) {
        return 'EXPIRY';
      }
      if (source.indexOf('HMS') >= 0) {
        return 'HMS';
      }
      if (source.indexOf('TMS') >= 0) {
        return 'TMS';
      }
      if (source.indexOf('EMS') >= 0) {
        return 'EMS';
      }
      return 'EMS';
    },
    scheduleSelectionKey: function (item) {
      if (!item) {
        return '';
      }
      if (item._selectionKey) {
        return item._selectionKey;
      }
      const source = this.scheduleSource(item);
      const scheduleId = this.valueFrom(item, ['cbid', 'scheduleid', 'ScheduleID', 'jobno', 'JobNo']);
      return `${source}:${scheduleId || item._scheduleKey || ''}`;
    },
    normalizeScheduleItem: function (item, index) {
      const normalized = Object.assign({}, item);
      const source = this.scheduleSource(item);
      const rawEquipment = this.scheduleVehicleNumber(item);
      const vehicleGrouping = this.vehicleGroupingForItem(item, rawEquipment);
      const equipment = (vehicleGrouping && (vehicleGrouping.lorryNo || vehicleGrouping.vehicleCode)) || rawEquipment;
      const hasSeparateEquipmentColumns =
        this.normalizedKey(item.col1title).indexOf('EQUIPMENT #') >= 0 &&
        this.normalizedKey(item.col2title).indexOf('EQUIPMENT CODE') >= 0;
      const equipmentDisplay =
        vehicleGrouping && (vehicleGrouping.lorryNo || vehicleGrouping.vehicleCode) ?
          this.combinedGanttColumnValue(vehicleGrouping.lorryNo, vehicleGrouping.vehicleCode)
        : hasSeparateEquipmentColumns ? this.combinedGanttColumnValue(item.ccol1text, item.ccol2text)
        : String(item.ccol1text || equipment || '').trim();
      const driverDisplay =
        hasSeparateEquipmentColumns ?
          String(item.ccol3text || '').trim()
        : String(item.ccol2text || item.ccol3text || '').trim();
      const explicitCategory = this.valueFrom(item, [
        'category',
        'Category',
        'vehiclecategory',
        'VehicleCategory',
        'Vehicle Category',
        'categorycode',
        'CategoryCode',
      ]);
      const sourceCategory = source === 'EMS' ? String(item.pcol1text || '').trim() : '';
      const sourceCategoryDescription = source === 'EMS' ? String(item.pcol2text || '').trim() : '';
      const categoryValue = explicitCategory || (vehicleGrouping && vehicleGrouping.category) || sourceCategory;
      const categoryDisplay = this.categoryDisplayValue(
        categoryValue,
        categoryValue === sourceCategory ? sourceCategoryDescription : '',
      );
      const customer =
        this.valueFrom(item, [
          'customer',
          'Customer',
          'customerName',
          'CustomerName',
          'CustName',
          'Billing Customer',
        ]) || this.columnValueByTitle(item, ['customer', 'client', 'consignee', 'shipper', 'billing']);
      const explicitTeam = this.valueFrom(item, [
        'team',
        'Team',
        'operatorTeam',
        'Operator Team',
        'teamName',
        'Team Name',
      ]);
      const sourceTeam =
        source === 'HMS' || source === 'TMS' ? this.combinedGanttColumnValue(item.pcol1text, item.pcol2text) : '';
      const team = explicitTeam || sourceTeam || (vehicleGrouping && vehicleGrouping.team);
      const collectionArea =
        this.valueFrom(item, [
          'collectionArea',
          'Collection Area',
          'pickupArea',
          'Pickup Area',
          'fromArea',
          'From Area',
        ]) ||
        this.columnValueByTitle(item, ['collection', 'pickup', 'origin', 'from']) ||
        this.ganttSideValue(this.valueFrom(item, ['cbltext']));
      const deliveryArea =
        this.valueFrom(item, [
          'deliveryArea',
          'Delivery Area',
          'destinationArea',
          'Destination Area',
          'toArea',
          'To Area',
        ]) ||
        this.columnValueByTitle(item, ['delivery', 'destination', 'dropoff', 'to']) ||
        this.ganttSideValue(this.valueFrom(item, ['cbrtext']));
      const plannedSlot =
        this.valueFrom(item, ['plannedSlot', 'Planned Slot', 'slot', 'Slot', 'plannedslot']) ||
        this.plannedSlotValue(item);
      const availability = this.valueFrom(item, [
        'availability',
        'Availability',
        'isAvailable',
        'IsAvailable',
        'Vehicle Availability',
      ]);
      const scheduleEndValue = this.valueFrom(item, [
        'cbend',
        'endtime',
        'EndTime',
        'enddatetime',
        'EndDateTime',
        'todatetime',
        'ToDateTime',
      ]);

      normalized._equipment = equipment;
      normalized._category = String(categoryValue || '').trim();
      normalized._categoryLabel = String(categoryDisplay || categoryValue || '').trim();
      normalized._source = source;
      normalized._sourceLabel = this.sourceLabel(source);
      normalized._customer = String(customer || '').trim();
      normalized._team = String(team || '').trim();
      normalized._collectionArea = String(collectionArea || '').trim();
      normalized._deliveryArea = String(deliveryArea || '').trim();
      normalized._plannedSlot = String(plannedSlot || '').trim();
      normalized._availability = String(availability || '').trim();
      normalized._isUnavailable = ['N', 'NO', 'UNAVAILABLE', 'NOT AVAILABLE'].indexOf(this.normalizedKey(availability)) >= 0;
      const scheduleId = this.valueFrom(item, ['cbid', 'scheduleid', 'ScheduleID', 'jobno', 'JobNo']);
      normalized._scheduleKey = `${scheduleId || 'row'}-${index}`;
      normalized._selectionKey = `${source}:${scheduleId || normalized._scheduleKey}`;
      normalized._vehicleKey =
        (vehicleGrouping && vehicleGrouping.vehicleKey) ||
        `${source}:${this.normalizedKey(equipment || item.cid || normalized._scheduleKey)}`;
      const scheduleStartDate = new Date(
        this.valueFrom(item, [
          'cbstart',
          'starttime',
          'StartTime',
          'startdatetime',
          'StartDateTime',
          'fromdatetime',
          'FromDateTime',
        ]),
      );
      const scheduleEndDate = new Date(scheduleEndValue);
      const rawDuration = parseFloat(normalized.cbduration);
      if (
        !isNaN(scheduleStartDate.getTime()) &&
        !isNaN(scheduleEndDate.getTime()) &&
        scheduleEndDate > scheduleStartDate
      ) {
        normalized.cbend = scheduleEndValue;
        normalized.cbduration = (scheduleEndDate.getTime() - scheduleStartDate.getTime()) / 3600000;
      } else if (source === 'EMS' && !isNaN(rawDuration)) {
        // Legacy EMS_SCHEDULE returns duration as hours divided by 60.
        normalized.cbduration = rawDuration * 60;
      }
      const scheduleStart = scheduleStartDate.getTime();
      const scheduleEnd = new Date(normalized.cbend || scheduleEndValue).getTime();
      const normalizedDuration = parseFloat(normalized.cbduration);
      normalized._scheduleStart = scheduleStart;
      normalized._scheduleEnd =
        !isNaN(scheduleEnd) && scheduleEnd > scheduleStart ?
          scheduleEnd
        : !isNaN(normalizedDuration) ?
          scheduleStart + Math.max(0, normalizedDuration) * 60 * 60 * 1000
        : NaN;
      normalized._plannedStartDate =
        isNaN(normalized._scheduleStart) ? '' : new Date(normalized._scheduleStart).formatString('yyyy-MM-dd');
      normalized._plannedEndDate =
        isNaN(normalized._scheduleEnd) ? '' : new Date(normalized._scheduleEnd).formatString('yyyy-MM-dd');
      normalized.cbsourcecolor = this.sourceColor(source);
      normalized.col1title = '[Equipment #] Equipment Code';
      normalized.col2title = '[Driver Code] Name';
      normalized.col3title = '';
      normalized.pcol1text = categoryDisplay || equipment;
      normalized.pcol2text = '';
      normalized.pcol3text = '';
      normalized.ccol1text = equipmentDisplay;
      normalized.ccol2text = driverDisplay;
      normalized.ccol3text = '';
      normalized.cid = normalized._vehicleKey;
      if (!normalized.cbcolor) {
        normalized.cbcolor = this.sourceColor(source);
      }
      if (!normalized.cbprogresscolor) {
        normalized.cbprogresscolor = normalized.cbcolor;
      }
      return normalized;
    },
    uniqueScheduleValues: function (field) {
      const values = {};
      let hasUnassigned = false;
      this.normalizedGanttItems.forEach((item) => {
        const label = String(item[field] || '').trim();
        if (label) {
          values[this.normalizedKey(label)] = { key: label, label: label };
        } else {
          hasUnassigned = true;
        }
      });
      if (hasUnassigned) {
        values.UNASSIGNED = { key: 'UNASSIGNED', label: 'Unassigned' };
      }
      return Object.keys(values)
        .sort()
        .map((key) => values[key]);
    },
    boardGroupValue: function (item) {
      return this.groupValueForItem(item, this.boardGroupBy);
    },
    groupValueForItem: function (item, groupBy) {
      const values = {
        category: item._categoryLabel || item._category,
        team: item._team,
        customer: item._customer,
        collectionArea: item._collectionArea,
        deliveryArea: item._deliveryArea,
        plannedStartDate: item._plannedStartDate,
        plannedEndDate: item._plannedEndDate,
      };
      return String(values[groupBy] || '').trim();
    },
    menuByTitle: function (title) {
      return this.menuItems.find((menu) => menu.title === title) || { title: title };
    },
    isMenuVisible: function (title) {
      const menu = this.menuByTitle(title);
      return menu.visible !== false;
    },
    isMenuEnabled: function (title) {
      const menu = this.menuByTitle(title);
      return menu.enabled !== false;
    },
    scheduleJobNumber: function (item) {
      return String(
        this.valueFrom(item, ['jobno', 'JobNo', 'jobNo', 'jobnumber', 'Job Number', 'Job No']) || '',
      ).trim();
    },
    formatScheduleDate: function (value) {
      const date = new Date(value);
      return isNaN(date.getTime()) ? String(value || '') : date.formatString('yyyy-MM-dd HH:mm');
    },
    scheduleOverlapsRange: function (item) {
      const rangeStart = this.startDate ? new Date(this.startDate).getTime() : NaN;
      const rangeEnd = this.endDate ? new Date(this.endDate).getTime() : NaN;
      if (isNaN(rangeStart) || isNaN(rangeEnd)) {
        return true;
      }

      const start = new Date(
        this.valueFrom(item, [
          'cbstart',
          'starttime',
          'StartTime',
          'startdatetime',
          'StartDateTime',
          'fromdatetime',
          'FromDateTime',
        ]),
      ).getTime();
      if (isNaN(start)) {
        return false;
      }

      const endValue = this.valueFrom(item, [
        'cbend',
        'endtime',
        'EndTime',
        'enddatetime',
        'EndDateTime',
        'todatetime',
        'ToDateTime',
      ]);
      let end = endValue ? new Date(endValue).getTime() : NaN;
      if (isNaN(end)) {
        const duration = parseFloat(item.cbduration);
        end = isNaN(duration) ? start : start + Math.max(0, duration) * 60 * 60 * 1000;
      }

      if (end <= start) {
        return start >= rangeStart && start <= rangeEnd;
      }
      return end > rangeStart && start < rangeEnd;
    },
    formatScheduleEnd: function (item) {
      const endValue = this.valueFrom(item, [
        'cbend',
        'endtime',
        'EndTime',
        'enddatetime',
        'EndDateTime',
        'todatetime',
        'ToDateTime',
      ]);
      if (endValue) {
        return this.formatScheduleDate(endValue);
      }
      const start = new Date(
        this.valueFrom(item, [
          'cbstart',
          'starttime',
          'StartTime',
          'startdatetime',
          'StartDateTime',
          'fromdatetime',
          'FromDateTime',
        ]),
      );
      const duration = parseFloat(item.cbduration);
      if (isNaN(start.getTime()) || isNaN(duration)) {
        return '';
      }
      return this.formatScheduleDate(new Date(start.getTime() + duration * 60 * 60 * 1000));
    },
    timelineStartDateChanged: function (value) {
      if (!value) {
        this.startDate = null;
        return;
      }
      const date = new Date(value);
      date.setHours(0, 0, 0, 0);
      this.startDate = date;
      if (this.endDate && new Date(this.endDate).getTime() < date.getTime()) {
        const endDate = new Date(date);
        endDate.setDate(endDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        this.endDate = endDate;
      }
    },
    timelineZoomChanged: function (interval) {
      this.timelineInterval = interval;
    },
    timelineEndDateChanged: function (value) {
      if (!value) {
        this.endDate = null;
        return;
      }
      const date = new Date(value);
      date.setHours(23, 59, 59, 999);
      if (this.startDate && date.getTime() < new Date(this.startDate).getTime()) {
        date.setTime(new Date(this.startDate).getTime());
        date.setDate(date.getDate() + 6);
        date.setHours(23, 59, 59, 999);
      }
      this.endDate = date;
    },
    clearFilters: function () {
      this.selectedCategories = [];
      this.selectedSources = [];
      this.selectedCustomers = [];
      this.selectedTeams = [];
      this.selectedCollectionAreas = [];
      this.selectedDeliveryAreas = [];
      this.scheduleFilterChanged();
    },
    scheduleItemAriaLabel: function (item) {
      const title = item.cbtext || item._equipment || 'Scheduled event';
      const source = item._sourceLabel || this.sourceLabel(this.scheduleSource(item));
      const equipment = item._equipment || item.ccol1text || '';
      const access = this.scheduleSource(item) === 'EMS' ? 'Editable' : 'View only';
      return [
        title,
        source,
        equipment ? `Vehicle ${equipment}` : '',
        `Starts ${this.formatScheduleDate(item.cbstart)}`,
        `Ends ${this.formatScheduleEnd(item)}`,
        access,
      ]
        .filter((value) => value)
        .join(', ');
    },
    focusFirstControl: function (container) {
      if (!container || !container.querySelector) {
        return;
      }
      const control = container.querySelector(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (control && control.focus) {
        control.focus();
      }
    },
    toggleScheduleFilters: function () {
      if (!this.scheduleFiltersReady) {
        return;
      }
      if (this.showScheduleFilters) {
        this.closeScheduleFilters(true);
        return;
      }
      this.showRefreshDropdown = false;
      this.showScheduleFilters = true;
      this.$nextTick(() => {
        this.updateScheduleFiltersPosition();
        this.focusFirstControl(this.$refs.scheduleFiltersPopup);
      });
    },
    updateScheduleFiltersPosition: function () {
      const button = this.$refs.scheduleFiltersButton;
      const toolbar = this.$el ? this.$el.querySelector('.schedule-toolbar-card') : null;
      if (!button || !toolbar) {
        return;
      }

      const buttonRect = button.getBoundingClientRect();
      const toolbarRect = toolbar.getBoundingClientRect();
      const toolbarWidth = toolbarRect.width;
      const preferredWidth = Math.min(680, toolbarWidth);
      const minimumAnchoredWidth = Math.min(420, preferredWidth);
      let left = Math.max(0, buttonRect.left - toolbarRect.left);

      if (toolbarWidth - left < minimumAnchoredWidth) {
        left = Math.max(0, toolbarWidth - preferredWidth);
      }

      this.scheduleFiltersPosition = {
        left: `${Math.round(left)}px`,
        width: `${Math.round(Math.min(preferredWidth, toolbarWidth - left))}px`,
      };
    },
    closeScheduleFilters: function (returnFocus) {
      this.showScheduleFilters = false;
      if (returnFocus) {
        this.$nextTick(() => {
          if (this.$refs.scheduleFiltersButton) {
            this.$refs.scheduleFiltersButton.focus();
          }
        });
      }
    },
    toggleRefreshDropdown: function () {
      if (this.showRefreshDropdown) {
        this.closeRefreshDropdown(true);
        return;
      }
      this.showScheduleFilters = false;
      this.showRefreshDropdown = true;
      this.$nextTick(() => this.focusFirstControl(this.$refs.scheduleRefreshMenu));
    },
    closeRefreshDropdown: function (returnFocus) {
      this.showRefreshDropdown = false;
      if (returnFocus) {
        this.$nextTick(() => {
          if (this.$refs.scheduleRefreshButton) {
            this.$refs.scheduleRefreshButton.focus();
          }
        });
      }
    },
    cancelRefreshClicked: function () {
      this.refreshIntervalClicked(null, null);
      this.closeRefreshDropdown(true);
    },
    scheduleKeyDown: function (event) {
      if (!event || event.key !== 'Escape') {
        return;
      }
      if (this.showScheduleFilters) {
        event.preventDefault();
        this.closeScheduleFilters(true);
      } else if (this.showRefreshDropdown) {
        event.preventDefault();
        this.closeRefreshDropdown(true);
      }
    },
    scheduleClickAway: function (event) {
      const target = event.target;
      const filterPopup = this.$refs.scheduleFiltersPopup;
      const filtersButton = this.$refs.scheduleFiltersButton;
      const refreshContainer = this.$refs.scheduleRefreshContainer;

      if (
        this.showScheduleFilters &&
        (!filterPopup || !filterPopup.contains(target)) &&
        (!filtersButton || !filtersButton.contains(target))
      ) {
        this.closeScheduleFilters(false);
      }
      if (this.showRefreshDropdown && (!refreshContainer || !refreshContainer.contains(target))) {
        this.closeRefreshDropdown(false);
      }

      ['scheduleStartDatePicker', 'scheduleEndDatePicker'].forEach((pickerRef) => {
        const picker = this.$refs[pickerRef];
        if (!picker || !picker.showCalendar) {
          return;
        }
        if (
          (!picker.$el || !picker.$el.contains(target)) &&
          (!picker.teleportedCalendar || !picker.teleportedCalendar.contains(target))
        ) {
          picker.showCalendar = false;
        }
      });
    },
    scheduleFilterChanged: function () {
      this.getGanttDetails();
    },
    loadScheduleFilterReferences: async function () {
      this.filterReferencesLoading = true;
      this.filterReferencesFailed = false;

      const [vehicleGroupingList, categoryList] = await Promise.all([
        this.$apiCallV2('/Master/GetListMaster', {
          masterfile: 'VEHICLETEAMMANAGEMENT_DISPLAY',
        }).catch(() => null),
        this.$apiCallV2('/EquipmentSchedule/GetVehicleCategory').catch(() => null),
      ]);

      if (Array.isArray(vehicleGroupingList)) {
        this.vehicleGroupingList = vehicleGroupingList;
      } else {
        this.vehicleGroupingList = [];
        this.referenceErrors.push('Vehicle grouping');
        this.filterReferencesFailed = true;
      }

      if (Array.isArray(categoryList)) {
        this.categoryList = categoryList
          .map((category) => ({
            CategoryCode: this.valueFrom(category, ['CategoryCode', 'Category', 'categorycode']),
            CategoryDesc: this.valueFrom(category, ['CategoryDesc', 'Category Description', 'Description', 'CategoryCode']),
          }))
          .filter((category) => category.CategoryCode);
      } else {
        this.categoryList = [];
        this.referenceErrors.push('Vehicle category');
        this.filterReferencesFailed = true;
      }

      this.filterReferencesLoading = false;
    },
    loadScheduleSupportingReferences: async function () {
      const [moduleLegendResults, destinationList, vehicleList, availabilityList, reasoningList] = await Promise.all([
        Promise.all([
          this.getModuleLegendItems('/EquipmentSchedule/GetLegends?viewType=EMS_SCHEDULE'),
          this.getModuleLegendItems('/VehicleSchedule/GetLegends?viewType=TEAM_PM'),
          this.getModuleLegendItems('/TruckingSchedule/GetLegends?viewType=TEAM_TR'),
          this.getModuleLegendItems('/EquipmentSchedule/GetLegends?viewType=POLICY'),
        ]),
        this.$apiCallV2(`/EquipmentSchedule/GetListMaster?master=destination`).catch(() => []),
        this.$apiCallV2(`/EquipmentSchedule/GetListMaster?master=vehicle`).catch(() => []),
        this.$apiCallV2(`/EquipmentSchedule/GetListMaster?master=EMS_SCHEDULE_AVAILABLE`).catch(() => []),
        this.$apiCallV2(`/EquipmentSchedule/GetListMaster?master=EMS_SCHEDULE_REASON`).catch(() => []),
      ]);

      this.legendsItems = moduleLegendResults[0];
      this.moduleLegendItems = {
        EMS: moduleLegendResults[0],
        HMS: moduleLegendResults[1],
        TMS: moduleLegendResults[2],
        EXPIRY: moduleLegendResults[3],
      };
      this.destinationList = Array.isArray(destinationList) ? destinationList : [];
      this.vehicleList = Array.isArray(vehicleList) ? vehicleList : [];
      this.availabilityList = Array.isArray(availabilityList) ? availabilityList : [];
      this.reasoningList = Array.isArray(reasoningList) ? reasoningList : [];
    },
    getGanttScrollPosition: function () {
      const gantt = this.$refs.scheduleGantt;
      const scrollContainer = gantt && gantt.$refs ? gantt.$refs.ganttMain : null;
      return scrollContainer ? { left: scrollContainer.scrollLeft, top: scrollContainer.scrollTop } : null;
    },
    restoreGanttScrollPosition: function (position) {
      if (!position) {
        return;
      }
      this.$nextTick(() => {
        const gantt = this.$refs.scheduleGantt;
        const scrollContainer = gantt && gantt.$refs ? gantt.$refs.ganttMain : null;
        if (scrollContainer) {
          scrollContainer.scrollLeft = position.left;
          scrollContainer.scrollTop = position.top;
        }
      });
    },
    changeViewMode: function (mode) {
      this.viewMode = mode;
    },
    selectTimelineGroup: function (option) {
      if (option && option.key) {
        const scrollPosition = this.getGanttScrollPosition();
        this.timelineGroupBy = option.key;
        this.timelineGroupSelection = option;
        this.boardGroupBy = option.key;
        this.boardGroupSelection = option;
        this.restoreGanttScrollPosition(scrollPosition);
      }
    },
    selectBoardGroup: function (option) {
      if (option && option.key) {
        this.boardGroupBy = option.key;
        this.boardGroupSelection = option;
        this.timelineGroupBy = option.key;
        this.timelineGroupSelection = option;
      }
    },
    openScheduleActions: function () {
      if (!this.hasSelectedSchedule) {
        return;
      }
      this.showScheduleFilters = false;
      this.editScheduleLoading = false;
      this.rightPanelMode = 'actions';
      this.popupTitle = 'Schedule Actions';
      this.showRightMenu = true;
    },
    clearSelectedSchedule: function () {
      this.selectedSchedule = [];
      this.scheduleID = '';
      this.selectedVehicleCode = [];
      if (this.rightPanelMode === 'actions') {
        this.showRightMenu = false;
      }
    },
    ganttTaskClicked: async function (e) {
      const clickedKey = this.scheduleSelectionKey(e);
      if (this.hasSelectedSchedule && clickedKey && clickedKey === this.selectedScheduleKey) {
        this.clearSelectedSchedule();
        return;
      }
      this.selectedSchedule = e;
      this.scheduleID = this.selectedSchedule['cbid'] || this.selectedSchedule['scheduleid'] || '';
      const vehicleNumber = this.scheduleVehicleNumber(this.selectedSchedule);
      this.selectedVehicleCode = vehicleNumber ? [vehicleNumber] : [];
      this.openScheduleActions();
    },
    ganttRowClicked: function (items) {
      if (Array.isArray(items) && items.length > 0) {
        this.ganttTaskClicked(items[0]);
      }
    },
    filterConfirm: function (items) {
      this.selectedVehicle = items;
      this.selectedVehicleCode = this.selectedVehicle
        .map((a) => this.scheduleVehicleNumber(a))
        .filter((value) => value);
    },
    menuClicked: async function (menu) {
      switch (menu['title']) {
        case 'New Schedule':
          this.showScheduleFilters = false;
          this.editScheduleLoading = false;
          this.clearSelectedSchedule();
          this.rightPanelMode = 'edit';
          this.clearScheduleForm();
          this.showRightMenu = true;
          this.popupTitle = menu['title'];
          break;
        case 'Edit Schedule':
          if (!this.selectedSchedule || (Array.isArray(this.selectedSchedule) && this.selectedSchedule.length == 0)) {
            this.$showAlert('error', 'No Schedule Selected', 'Please select Schedule to edit');
          } else if (this.scheduleSource(this.selectedSchedule) !== 'EMS') {
            this.$showAlert(
              'error',
              'Read-only Event',
              'Haulage Job, Trucking Job, and policy expiry events are view-only here.',
            );
          } else {
            this.showScheduleFilters = false;
            this.rightPanelMode = 'edit';
            this.popupTitle = menu['title'];
            this.showRightMenu = true;
            this.editScheduleLoading = true;
            try {
              await this.populateSchedule(this.scheduleID);
            } catch (error) {
              this.$showAlert('error', 'Unable to Load Schedule', 'The selected schedule details could not be loaded.');
              this.showRightMenu = false;
            } finally {
              this.editScheduleLoading = false;
            }
            //this.populateSchedule(this.selectedSchedule['cbid']);
          }
          break;
        case 'Delete Schedule':
          this.showScheduleFilters = false;
          this.editScheduleLoading = false;
          if (!this.selectedSchedule || (Array.isArray(this.selectedSchedule) && this.selectedSchedule.length == 0)) {
            this.$showAlert('error', 'No Schedule Selected', 'Please select Schedule to delete');
          } else if (this.scheduleSource(this.selectedSchedule) !== 'EMS') {
            this.$showAlert(
              'error',
              'Read-only Event',
              'Haulage Job, Trucking Job, and policy expiry events are view-only here.',
            );
          } else {
            let deleting = await this.$showAlert(
              'warning',
              'Delete Equipment Schedule',
              `Are you sure you want to delete Equipment Schedule of id: ${this.selectedSchedule['cbid']}?`,
              [
                { text: 'Delete', textColor: 'white', color: 'red' },
                { text: 'Cancel', textColor: 'black', color: 'lightgray' },
              ],
            );
            if (deleting == 'Delete') {
              this.deleteScheduleLoading = true;
              this.$fullscreenLoading('Deleting Schedule');
              try {
                //parameters correct
                let result = await this.$apiCallV2('/EquipmentSchedule/SaveVehicleSchedule', {
                  action: 'D',
                  scheduleid: this.selectedSchedule['cbid'],
                  lorrylist: this.selectedVehicleCode,
                });
                const deleteResult = Array.isArray(result) ? result[0] : null;
                if (deleteResult && deleteResult['Results'] == 0) {
                  // Delete successful
                  const deletedScheduleID = this.selectedSchedule['cbid'];
                  this.clearSelectedSchedule();
                  this.$showAlert(
                    'success',
                    'Equipment Schedule Deleted',
                    `Successfully deleted equipment schedule ${deletedScheduleID}`,
                  );
                  await this.getGanttDetails();
                } else {
                  // Delete error
                  this.$showAlert(
                    'error',
                    'Equipment Schedule NOT Deleted',
                    `There was an error deleting equipment schedule ${this.selectedSchedule['cbid']}`,
                  );
                }
              } catch (error) {
                this.$showAlert(
                  'error',
                  'Equipment Schedule NOT Deleted',
                  'The delete request failed. Please try again.',
                );
              } finally {
                this.deleteScheduleLoading = false;
                this.$fullscreenLoading();
              }
            }
          }
          break;
      }
    },
   clearScheduleForm: function () {
     this.editScheduleLoading = false;
     this.saveScheduleLoading = false;
      this.selectedAvailability = null;
      this.selectedReason = null;
      this.selectedVehicle = [];
      this.selectedVehicleCode = [];
      this.remarks = '';
      this.starttime = new Date();
      this.endtime = null;
      this.fromLoc = null;
      this.toLoc = null;
      this.scheduleID = '';
    },
    //not done
    populateSchedule: async function (scheduleID) {
      let details = (await this.$apiCallV2(`/EquipmentSchedule/GetVehicleSchedule?scheduleid=${scheduleID}`))[0];
      this.starttime = details['FromDateTime'] ? new Date(details['FromDateTime']) : null;
      this.endtime = details['ToDateTime'] ? new Date(details['ToDateTime']) : null;
      this.fromLoc = this.destinationList.getSelected(details['FromDest'], 'Destination Code');

      this.toLoc = this.destinationList.getSelected(details['ToDest'], 'Destination Code');
      // this.toLoc = this.populateObject(this.destinationList, 'Destination Code', details['ToDest']);
      this.selectedReason = this.reasoningList.getSelected(details['ReasonType'], 'Reason Type');
      this.selectedAvailability = this.availabilityList.getSelected(
        details['Availability'] == 'Y' ? 'Yes'
        : details['Availability'] == 'N' ? 'No'
        : '',
        'Availability',
      );
      this.remarks = details['Remarks'];

      this.filterConfirm(this.vehicleList.filter((v) => v['Inventory #'] == details['Lorry_No']));
    },
    refreshCountdown: function () {
      this.refreshTime--;
      if (this.refreshTime < 0) {
        this.refreshTime = this.refreshTimeStart;
      } else if (this.refreshTime == 0) {
        this.getGanttDetails();
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    refreshIntervalClicked: function (e, seconds) {
      if (seconds) {
        this.refreshTime = seconds;
        if (e) {
          this.refreshTimeStart = seconds;
        }
        if (this.refreshInterval) {
          clearInterval(this.refreshInterval);
        }

        this.refreshInterval = setInterval(this.refreshCountdown, 1000);
      } else {
        if (e) {
          this.getGanttDetails();
        } else {
          clearInterval(this.refreshInterval);
          this.refreshInterval = null;
          this.refreshTimeStart = 0;
        }
      }

      this.closeRefreshDropdown(!!e);
    },
    getGanttDetails: async function () {
      const startDateTime = this.startDate ? new Date(this.startDate).getTime() : NaN;
      const endDateTime = this.endDate ? new Date(this.endDate).getTime() : NaN;
      if (isNaN(startDateTime) || isNaN(endDateTime) || startDateTime > endDateTime) {
        this.$showAlert('error', 'Invalid Date Range', 'The timeline start must be before the timeline end.');
        return;
      }
      if (this.scheduleDataLoading) {
        return;
      }
      this.scheduleDataLoading = true;
      this.clearSelectedSchedule();
      this.$fullscreenLoading('Getting Details');

      try {
        this.ganttItems = [];
        this.sourceErrors = [];
        const serverStart = new Date(this.startDate);
        const serverEnd = new Date(this.endDate);
        serverStart.setDate(serverStart.getDate() - 30);
        serverEnd.setDate(serverEnd.getDate() + 30);
        const serverStartText = serverStart.formatString('yyyy-MM-dd HH:mm');
        const serverEndText = serverEnd.formatString('yyyy-MM-dd HH:mm');
        const allSources = [
          { viewtype: 'EMS_SCHEDULE', source: 'EMS', label: 'Workshop Job' },
          { viewtype: 'TEAM_PM', source: 'HMS', label: 'Haulage Job' },
          { viewtype: 'TEAM_TR', source: 'TMS', label: 'Trucking Job' },
          { viewtype: 'POLICY', source: 'EXPIRY', label: 'Policy expiry' },
        ];
        const selectedSourceKeys = this.selectedSources.map((item) =>
          this.normalizedKey(item.key || item.label),
        );
        const sources =
          selectedSourceKeys.length > 0 ?
            allSources.filter((source) => selectedSourceKeys.indexOf(source.source) >= 0)
          : allSources;
        const sourceResults = await Promise.all(
          sources.map(async (source) => {
            try {
              const result = await this.$apiCallV2(
                `/EquipmentSchedule/GanttViewTAS?starttime=${serverStartText}&endtime=${serverEndText}&viewtype=${source.viewtype}`,
                null,
                null,
                {},
              );
              if (!Array.isArray(result)) {
                this.sourceErrors.push(source.label);
                return [];
              }
              return result.map((item) => Object.assign({}, item, { source: source.source }));
            } catch (error) {
              this.sourceErrors.push(source.label);
              return [];
            }
          }),
        );
        this.ganttItems = sourceResults.reduce((items, result) => items.concat(result), []);
        this.lastRefresh = new Date().formatString('yyyy-MM-dd HH:mm');
      } catch (error) {
        this.$showAlert(
          'error',
          'Unable to Refresh Schedule',
          'The latest schedule data could not be loaded. Please try again.',
        );
      } finally {
        this.scheduleDataLoading = false;
        this.$fullscreenLoading();

        if (this.refreshTimeStart > 0) {
          this.refreshIntervalClicked(null, this.refreshTimeStart);
        }
      }
    },
    saveClicked: async function () {
      if (this.editScheduleLoading || this.saveScheduleLoading) {
        return;
      }
      let isNotValid = this.checkRequiredFields();

      if (this.selectedVehicle.length <= 0) {
        this.$showAlert('error', 'Compulsory field', 'Must select at least 1 vehicle');
        return;
      }

      if (!isNotValid) {
        let action = this.popupTitle[0];
        let dataToPost = {
          reasontype: this.selectedReason ? this.selectedReason['Reason Type'] : '',
          availability: this.selectedAvailability ? this.selectedAvailability['Availability'] : '',
          fromdest: this.fromLoc ? this.fromLoc['Destination Code'] : '',
          todest: this.toLoc ? this.toLoc['Destination Code'] : '',
          lorrylist: this.selectedVehicleCode,
          action: action,
          fromdatetime: this.starttime ? this.starttime.formatString('yyyy-MM-dd HH:mm') : '',
          todatetime: this.endtime ? this.endtime.formatString('yyyy-MM-dd HH:mm') : '',
          remarks: this.remarks,
          scheduleid: action == 'N' ? '' : this.scheduleID,
        };
        this.saveScheduleLoading = true;
        this.$fullscreenLoading('Saving Schedule');
        try {
          let result = await this.$apiCallV2('/EquipmentSchedule/SaveVehicleSchedule', dataToPost);
          const saveResult = Array.isArray(result) ? result[0] : null;

          if (saveResult && saveResult['Results'] == 0) {
            // Save successful
            this.clearSelectedSchedule();
            await this.getGanttDetails();
            if (action == 'N') {
              this.$showAlert(
                'success',
                'Successfully created equipment schedule. Reason: ',
                this.selectedReason['Reason Type'],
              );
            } else {
              this.$showAlert(
                'success',
                'Successfully edited equipment schedule. Reason: ',
                this.selectedReason['Reason Type'],
              );
            }
            this.showRightMenu = false;
          } else {
            // Save error
            const errorMessage = saveResult ? saveResult['ErrMsg'] : 'The save request returned no result.';
            if (action == 'N') {
              this.entryError.push(`There was an error creating schedule: ${errorMessage}`);
            } else {
              this.entryError.push(`There was an error editing schedule: ${errorMessage}`);
            }
          }
        } catch (error) {
          this.entryError.push(
            action == 'N'
              ? 'There was an error creating schedule. Please try again.'
              : 'There was an error editing schedule. Please try again.',
          );
        } finally {
          this.saveScheduleLoading = false;
          this.$fullscreenLoading();
        }
      }
    },
    closeMenu: function () {
      this.editScheduleLoading = false;
      this.saveScheduleLoading = false;
      this.showRightMenu = false;
      this.clearScheduleForm();
    },
    checkRequiredFields: function () {
      this.clearEntryMessages();
      if (!this.starttime) {
        this.starttimeError = 'Start Date is required!';
        this.entryRequired.push({ key: 'starttime', value: 'Start Date is required!' });
      }
      if (!this.endtime) {
        this.endtimeError = 'End Date is required!';
        this.entryRequired.push({ key: 'endtime', value: 'End Date is required!' });
      }
      if (this.starttime && this.endtime && new Date(this.endtime) <= new Date(this.starttime)) {
        this.endtimeError = 'End Date must be after Start Date!';
        this.entryRequired.push({ key: 'endtime', value: 'End Date must be after Start Date!' });
      }
      if (!this.selectedReason) {
        this.reasonError = 'Reason is required!';
        this.entryRequired.push({ key: 'reason', value: 'Reason is required!' });
      }
      if (!this.selectedAvailability) {
        this.availabilityError = 'Schedule Availability is required!';
        this.entryRequired.push({ key: 'availability', value: 'Schedule Availability is required!' });
      }
      if (!this.fromLoc) {
        this.fromLocError = 'Start Location is required!';
        this.entryRequired.push({ key: 'fromLoc', value: 'Start Location is required!' });
      }
      if (!this.toLoc) {
        this.toLocError = 'Destination is required!';
        this.entryRequired.push({ key: 'toLoc', value: 'Destination is required!' });
      }
      if (this.selectedVehicleCode.length == 0) {
        this.vehicleError = 'Vehicle is required!';
        this.entryRequired.push({ key: 'selectedVehicleCode', value: 'Vehicle is required!' });
      }

      return this.entryRequired.length > 0;
    },
    clearEntryMessages: function () {
      this.entryRequired = [];
      this.entrySuccess = [];
      this.entryMessages = [];
      this.entryError = [];

      this.starttimeError = '';
      this.endtimeError = '';
      this.reasonError = '';
      this.availabilityError = '';
      this.fromLocError = '';
      this.toLocError = '';
      this.vehicleError = '';
    },
  },
  async mounted() {
    document.addEventListener('mousedown', this.scheduleClickAway, true);
    document.addEventListener('mousedown', this.rightMenuPointerPressed, true);
    document.addEventListener('keydown', this.scheduleKeyDown);
    window.addEventListener('mouseup', this.rightMenuPointerReleased, true);
    window.addEventListener('blur', this.rightMenuPointerReleased);
    window.addEventListener('resize', this.updateScheduleFiltersPosition);
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Equipment Schedule') {
        x.data.forEach((k) => {
          //key in menu same as control value from api
          let selectedMenu = this.menuItems.find((m) => m['key'] == k['Control']);
          if (selectedMenu) {
            selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
          }
        });
      }
    });

    this.loadScheduleFilterReferences();
    this.loadScheduleSupportingReferences();
    await this.$nextTick();
    this.getGanttDetails();
  },
  watch: {
    showRightMenu: function (val) {
      const width = this.clampRightMenuWidth(this.rightMenuWidth || this._rightMenuLiveWidth || 420);

      if (val) {
        this.rightMenuLayoutWidth = width;
        this.rightMenuLayoutOpen = true;
        this.rightMenuSlideOpen = true;
        this.$nextTick(() => this.applyRightMenuLiveWidth(width, true));
      } else {
        this.rightMenuSlideOpen = false;
        this.rightMenuLayoutOpen = false;
        this._rightMenuResizeActive = false;
        this._pendingRightMenuWidth = 0;
        this.$nextTick(() => this.applyRightMenuLiveWidth(width, false));
      }

      if (this.refreshInterval) {
        if (val) {
          clearInterval(this.refreshInterval);
        } else {
          this.refreshIntervalClicked(null, this.refreshTime);
        }
      }
      if (!val) {
        this.editScheduleLoading = false;
        this.clearEntryMessages();
        this.clearSelectedSchedule();
      }
    },
    rightMenuWidth: function (value) {
      if (this._rightMenuResizeActive) {
        return;
      }

      const width = this.clampRightMenuWidth(value || 420);
      this._rightMenuLiveWidth = width;
      this.$nextTick(() => this.applyRightMenuLiveWidth(width, this.rightMenuLayoutOpen));
    },
    // clear scheduleID when close gantt task azizi adding
    selectedSchedule: function (val) {
      if (!val || (Array.isArray(val) && val.length === 0)) {
        this.clearEntryMessages();
      }
    },
    filteredGanttItems: function (items) {
      if (
        this.hasSelectedSchedule &&
        !items.some((item) => this.scheduleSelectionKey(item) === this.selectedScheduleKey)
      ) {
        this.clearSelectedSchedule();
      }
    },
    scheduleSearchInput: function (value) {
      if (this.scheduleSearchTimer) {
        window.clearTimeout(this.scheduleSearchTimer);
      }
      this.scheduleSearchTimer = window.setTimeout(() => {
        this.scheduleSearchTimer = 0;
        this.scheduleSearchTerm = value;
      }, 180);
    },
    searchedGanttItems: function (items) {
      if (
        this.hasSelectedSchedule &&
        !items.some((item) => this.scheduleSelectionKey(item) === this.selectedScheduleKey)
      ) {
        this.clearSelectedSchedule();
      }
    },
  },
  beforeDestroy: function () {
    document.removeEventListener('mousedown', this.scheduleClickAway, true);
    document.removeEventListener('mousedown', this.rightMenuPointerPressed, true);
    document.removeEventListener('keydown', this.scheduleKeyDown);
    window.removeEventListener('mouseup', this.rightMenuPointerReleased, true);
    window.removeEventListener('blur', this.rightMenuPointerReleased);
    window.removeEventListener('resize', this.updateScheduleFiltersPosition);
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    if (this.ganttLayoutRefreshTimer) {
      window.clearTimeout(this.ganttLayoutRefreshTimer);
      this.ganttLayoutRefreshTimer = 0;
    }
    if (this.rightMenuResizeTimer) {
      window.clearTimeout(this.rightMenuResizeTimer);
      this.rightMenuResizeTimer = 0;
    }
    if (this._rightMenuResizeFrame) {
      window.cancelAnimationFrame(this._rightMenuResizeFrame);
      this._rightMenuResizeFrame = 0;
    }
    if (this.scheduleSearchTimer) {
      window.clearTimeout(this.scheduleSearchTimer);
      this.scheduleSearchTimer = 0;
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.equipment-schedule {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  min-height: 0;
  padding: 6px 6px 6px 46px;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  font-size: 12px;
  gap: 4px;

  button:focus-visible {
    outline: 2px solid #1f6fb2 !important;
    outline-offset: 2px;
  }

  > .gantt-container {
    background-color: transparent;
    padding: 0;
    height: 100%;
    width: auto;
    flex: 1 1 auto;
    margin-left: 0;
    transition: none;
    overflow-anchor: none;
    display: flex;
    flex-direction: column;
    position: relative;
    min-width: 0;
    gap: 5px;

    > .schedule-toolbar-card,
    > .schedule-chart-card {
      border-radius: 5px;
      background-color: white;
      box-sizing: border-box;
      padding: 5px 7px;
    }

    > .schedule-toolbar-card {
      position: relative;
      z-index: 110;
      flex: 0 0 auto;
    }

    > .schedule-chart-card {
      position: relative;
      z-index: 1;
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .schedule-toolbar-row {
      display: flex;
      align-items: center;
      gap: 5px;
    }

    .schedule-toolbar-main {
      flex-wrap: wrap;
      row-gap: 6px;
      justify-content: flex-start;
    }

    .date-title {
      flex: 0 0 auto;
      white-space: nowrap;
      font-weight: bold;
    }

    .picker-main {
      top: 0;
    }

    .schedule-date-picker {
      flex: 0 0 auto;
    }

    .schedule-toolbar-actions {
      display: flex;
      align-items: center;
      flex: 0 0 auto;
      flex-wrap: wrap;
      gap: 5px;
      margin-left: 0;
    }

    .new-schedule-button {
      flex: 0 0 auto;
      white-space: nowrap;
      margin-left: auto;
    }

    .segmented-control {
      display: inline-flex;
      align-items: stretch;
      flex: 0 0 auto;
      gap: 1px;
      background: lightgray;
      border: 1px solid lightgray;
      border-radius: 0.25rem;
      overflow: hidden;

      > button {
        padding: 0.25rem 0.5rem;
        border: 0 !important;
        border-radius: 0;
        background-color: #fafafa;
        color: #405064;
        transition: none;
        outline: 0 !important;
        box-shadow: none;
        &:focus-visible {
          outline: 2px solid #1f6fb2 !important;
          outline-offset: -2px;
        }
        &.active {
          background-color: #b3c4ff;
          color: #25345f;
        }
      }
    }

    .filter-button {
      background-color: #eef0ff;
      border-color: #8796c7;
      color: #25345f;
      white-space: nowrap;
    }

    .filter-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      margin-left: 4px;
      border-radius: 50%;
      background-color: #4f81bd;
      color: white;
      font-size: 10px;
    }

    .refresh-container {
      position: relative;
      flex: 0 0 auto;

      > button {
        width: 150px;
        box-sizing: border-box;
        white-space: nowrap;
      }

      > .refresh-dropdown {
        position: absolute;
        top: 100%;
        right: 0;
        background-color: white;
        width: 100%;
        box-sizing: border-box;
        z-index: 50;
        padding: 5px;
        border-width: 1px;
        border-style: solid;
        border-color: transparent black black black;

        > button {
          width: 100%;
          border-radius: 0;
          padding: 3px;
          white-space: nowrap;
        }

        > .refresh-now {
          margin: 5px 0;
        }

        > .cancel-refresh {
          background-color: red;
          color: white;
        }
      }
    }

    .chart-status-row {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
      padding: 2px 2px 3px;
    }

    .chart-status-right {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      margin-left: auto;
      flex: 0 1 auto;
    }

    .last-refresh {
      flex: 0 0 auto;
      white-space: nowrap;
      color: #64748b;
      font-size: 10px;
    }

    .source-warning {
      flex: 0 1 240px;
      color: #b00020;
      font-size: 10px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .schedule-sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    > .schedule-toolbar-card > .schedule-filters-backdrop {
      position: absolute;
      top: 100%;
      left: 0;
      z-index: 30;
      max-width: 680px;
      max-height: calc(100vh - 120px);
      padding-top: 4px;
    }

    .schedule-filters-popup {
      width: 100%;
      max-height: 100%;
      overflow: auto;
      padding: 6px;
      border: 1px solid #9da9c7;
      border-radius: 4px;
      background-color: white;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    }

    .schedule-filters-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 2px 4px;
      border-bottom: 1px solid #d5d5d5;

      > button {
        width: 22px;
        height: 22px;
        padding: 0;
        border: 0;
        background: transparent;
        font-size: 16px;
        line-height: 16px;
      }
    }

    .schedule-filters {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 5px;
      padding: 5px 0;

      > * {
        height: 78px;
        min-width: 0;
      }

      ::v-deep .filter-items {
        min-width: 0;
        margin: 0;
      }

      ::v-deep .filter-title {
        height: 17px;
      }

      ::v-deep .filter-title > button {
        margin: 0;
        padding: 0 2px;
      }

      ::v-deep .filter-details {
        top: 18px;
        height: calc(100% - 18px);
        margin-top: 0;
        padding: 1px 3px;
      }

      ::v-deep .filter-details > .item {
        padding: 1px 2px;
      }
    }

    .schedule-filters-footer {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 5px;
      padding-top: 5px;
      border-top: 1px solid #d5d5d5;
      font-size: 10px;

      > button {
        padding: 3px 7px;
      }

      > button:first-child {
        margin-right: auto;
      }
    }

    .chart-toolbar {
      display: flex;
      align-items: flex-end;
      flex-wrap: wrap;
      gap: 6px;
      padding: 0 2px 3px;
      font-size: 10px;
      font-weight: bold;
      min-width: 0;

      > .board-group-dropdown {
        flex: 1 1 180px;
        font-weight: normal;
      }
    }

    .chart-toolbar-right {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      flex: 0 0 auto;
      gap: 6px;
      margin-left: auto;
      justify-content: flex-end;
    }

    .chart-toolbar-control {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
      min-width: 0;

      &.group-by-control > .dropdown-main {
        width: 170px;
        max-width: 100%;
      }

      &.schedule-search-control {
        > .input-main {
          width: 170px;
          max-width: 100%;
        }
      }

      &.schedule-search-control,
      &.group-by-control {
        ::v-deep .input-main {
          font-weight: normal;
        }
      }
    }

    .schedule-chart-card > .gantt-with-legends {
      flex: 1 1 auto;
      min-height: 0;
      min-width: 0;
      display: flex;
      overflow: hidden;
      background: white;

      > .gantt-main {
        flex: 1 1 auto;
        min-width: 0;
        width: auto;
      }

      > .gantt-legends {
        flex: 0 0 145px;
        min-width: 0;
      }

      > .schedule-board {
        flex: 1 1 auto;
        height: 100%;
        display: flex;
        align-items: stretch;
        gap: 12px;
        overflow-x: auto;
        overflow-y: hidden;
        padding: 5px;
        background: white;
      }

      > .schedule-board .board-empty {
        color: #666;
        padding: 20px;
        text-align: center;
      }

      > .schedule-board .board-group {
        flex: 0 0 290px;
        min-height: 0;
        display: flex;
        flex-direction: column;
        margin-bottom: 0;
        border: 1px solid #d4e1eb;
        background: #f8fbfe;
      }

      > .schedule-board .board-group-title {
        background: transparent;
        border-bottom: 1px solid #d8e5ee;
        color: #263544;
        font-weight: bold;
        padding: 7px 8px;

        > span {
          color: #666;
          font-weight: normal;
          margin-left: 5px;
        }
      }

      > .schedule-board .board-cards {
        display: flex;
        flex: 1 1 auto;
        min-height: 0;
        flex-direction: column;
        overflow-x: hidden;
        overflow-y: auto;
        gap: 8px;
        padding: 6px 8px 8px;
      }

      > .schedule-board .board-card {
        flex: 0 0 auto;
        background: white;
        border: 1px solid #aeb9c5;
        border-radius: 1px;
        box-shadow: 0 1px 1px rgba(51, 65, 85, 0.1);
        cursor: pointer;
        min-height: 0;
        padding: 7px;
        text-align: left;
        transition: none !important;
        animation: none !important;

        > * {
          transition: none !important;
          animation: none !important;
        }

        &:hover {
          border-color: #718096;
          background: white;
        }

        &:focus-visible {
          outline: 2px solid #1f6fb2;
          outline-offset: 1px;
        }

        &.board-card-selected {
          outline: 3px solid #1f6fb2;
          outline-offset: -1px;
          background: white;
        }
      }

      .board-card-title {
        color: #1f2933;
        font-weight: bold;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 16px;
        min-height: 16px;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
      }

      .board-card-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 6px;
      }

      .board-card-tag {
        display: inline-flex;
        align-items: center;
        min-height: 18px;
        max-width: 100%;
        padding: 2px 5px 2px 6px;
        border: 1px solid #cbd5df;
        border-left: 3px solid var(--board-tag-color, #7f7f7f);
        border-radius: 1px;
        background: #f7f9fb;
        color: #334155;
        font-size: 10px;
        line-height: 12px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .board-card-window {
        display: flex;
        gap: 6px;
        min-width: 0;
        margin-top: 6px;
        padding: 5px 0;
        border-top: 1px solid #d5dde6;
        border-bottom: 1px solid #d5dde6;
        color: #334155;
        font-size: 10px;
        font-variant-numeric: tabular-nums;

        > strong {
          flex: 0 0 40px;
          color: #475569;
        }

        > span {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .board-card-details {
        display: flex;
        flex-direction: column;
        gap: 7px;
        margin-top: 7px;
        padding: 1px 0;
      }

      .board-card-detail {
        display: flex;
        gap: 6px;
        min-width: 0;
        line-height: 11px;

        > strong {
          flex: 0 0 68px;
          color: #475569;
          font-size: 10px;
          line-height: 11px;
        }

        > span {
          min-width: 0;
          overflow-wrap: anywhere;
          word-break: break-word;
          white-space: normal;
          color: #334155;
          font-size: 10px;
          line-height: 11px;
        }
      }

      > .gantt-legends-toggle {
        display: flex;
        align-self: flex-start;
        align-items: center;
        justify-content: center;
        flex: 0 0 22px;
        width: 22px;
        height: 36px;
        padding: 0;
        border: 1px solid #cbd5e1;
        border-radius: 2px;
        background-color: #f3f5f7;
        color: #475569;
        font-size: 16px;
        line-height: 1;

        > .gantt-legends-chevron {
          width: 12px;
          height: 12px;
          object-fit: contain;
        }
      }

      > .gantt-legends {
        width: 145px;
        height: fit-content;
        max-height: 100%;
        font-size: 10px;
        margin-left: 5px;
        overflow-x: hidden;
        overflow-y: auto;

        &.legends-collapsed {
          width: 0;
          flex-basis: 0;
          margin-left: 0;
          overflow-x: hidden;
        }

        > .gantt-legend-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 6px;
          font-weight: bold;
          color: #475569;

          > .gantt-legends-close {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            padding: 0;
            border: 1px solid #cbd5e1;
            border-radius: 2px;
            background-color: #f3f5f7;
            color: #475569;
            opacity: 0;
            pointer-events: none;

            > .gantt-legends-chevron {
              width: 12px;
              height: 12px;
              object-fit: contain;
              transform: rotate(180deg);
            }
          }
        }

        &:hover > .gantt-legend-header > .gantt-legends-close,
        > .gantt-legend-header > .gantt-legends-close:focus {
          opacity: 1;
          pointer-events: auto;
        }

        .legend-collapsed-swatches {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;

          > span {
            width: 14px;
            height: 14px;
            border-radius: 2px;
          }
        }

        > .gantt-legend-group {
          margin-bottom: 14px;
        }

        > .gantt-legend-group > .gantt-legend-group-title {
          display: flex;
          align-items: center;
          color: #475569;
          font-weight: bold;
          margin-bottom: 4px;
          overflow-wrap: anywhere;
        }

        .gantt-legend-group-color {
          flex: 0 0 10px;
          height: 10px;
          margin-right: 4px;
        }

        > .gantt-legend-group > .gantt-legends-items {
          display: flex;
          align-items: center;
          margin-bottom: 5px;

          > .gantt-legends-color {
            width: 20px;
            height: 20px;
            margin-right: 3px;
          }

          > div:last-child {
            min-width: 0;
            white-space: normal;
            word-break: normal;
            overflow-wrap: break-word;
            line-height: 1.25;
          }
        }
      }
    }
  }
}

.equipment-schedule.right-menu-layout-open {
  > .gantt-container {
    // The live inline margin is updated without rerendering Vue. This fallback
    // retains the committed width after the drag ends or after remounting.
    margin-right: var(--schedule-right-menu-layout-width);
  }

  > .gantt-container > .schedule-chart-card > .gantt-with-legends > .gantt-legends {
    flex: 0 0 0;
    width: 0;
    margin-left: 0;
    overflow: hidden;
  }
}

.schedule-action-panel {
  height: 100%;
  padding: 10px;
  overflow-y: auto;
  background-color: white;
}

.schedule-form-field {
  display: flex !important;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;

  > * {
    width: 100%;
  }
}

.scheduled-vehicle-readonly {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: 5px 0;
  padding: 6px;
  box-sizing: border-box;
  border: 1px solid #d5d5d5;
  background: #f7f8fa;
}

.scheduled-vehicle-label {
  color: #475569;
  font-size: 10px;
}

.scheduled-vehicle-value {
  overflow-wrap: anywhere;
}

.schedule-action-summary {
  padding-bottom: 10px;
  font-weight: bold;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.schedule-action-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
  border-top: 1px solid #d5d5d5;
  border-bottom: 1px solid #d5d5d5;

  > div {
    display: flex;
    gap: 8px;

    > strong {
      flex: 0 0 90px;
      color: #475569;
    }

    > span {
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }
}

.schedule-action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 12px;
}

.ems-title {
  width: 100%;
  min-height: 33px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 5px 36px 5px 5px;
  line-height: 1.3;
  white-space: normal;
  overflow-wrap: anywhere;
}

.ems-trip-container {
  background-color: white;
  border-radius: 5px;
  width: 100%;
  height: 100%;
  position: relative;

  flex-direction: column;
  overflow-y: auto;
  z-index: 98;
  width: 100%;
  display: flex;
  gap: 8px;
  padding: 6px;
  box-sizing: border-box;

  > * {
    width: 100%;
    padding: 0;
    display: flex;
  }

  > .edit-schedule-loading {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 100;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background-color: rgba(255, 255, 255, 0.9);
    color: #405064;
    font-weight: bold;
    pointer-events: auto;
  }

  > .edit-schedule-loading .edit-schedule-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #c7d2e4;
    border-top-color: #4f81bd;
    border-radius: 50%;
    animation: schedule-loader-spin 0.8s linear infinite;
  }

  > .vehicle-multiselect {
    &.is-disabled {
      opacity: 0.6;
      pointer-events: none;
    }
  }

  > .ems-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;

    > .button {
      width: 100%;
    }
  }

}

.right-menu {
  z-index: 998;
  background-color: #eaeaea;
  position: absolute !important;
  top: 6px;
  right: 6px;
  bottom: 6px;
  height: auto !important;
  width: var(--right-menu-live-width, 420px) !important;
  min-width: var(--right-menu-live-width, 420px) !important;
  max-width: var(--right-menu-live-width, 420px) !important;
  transform: translateX(var(--right-menu-live-width, 420px));
  transition: transform 0.3s ease !important;

  &.right-menu-slide-open {
    transform: translateX(0);
  }
}

.rowBox {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;

  > .schedule-form-field {
    flex: 1 1 160px;
    min-width: 0;
  }
}

@keyframes schedule-loader-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 900px) {
  .equipment-schedule > .gantt-container {
    > .schedule-toolbar-card,
    > .schedule-chart-card {
      padding: 7px;
    }

    .schedule-toolbar-main {
      align-items: center;
      flex-wrap: wrap;
    }

    .chart-toolbar-right {
      margin-left: 0;
    }

    .schedule-toolbar-actions {
      flex-wrap: wrap;
      margin-left: 0;
    }

    .new-schedule-button {
      margin-left: auto;
    }

    .last-refresh,
    .source-warning {
      flex-basis: auto;
    }

    .chart-status-right {
      width: 100%;
      margin-left: 0;
      justify-content: space-between;
    }

    .schedule-filters {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}
</style>
